/**
 * User Dashboard - Flourishing Plants
 * Plain JavaScript (ES module), same pattern as main app.
 * Logged-out visitors can browse a read-only preview dashboard.
 * Premium users get their own Firestore-backed dashboard.
 */

import { subscriptionManager, initializeSubscription } from '../src/modules/subscription.js';
import { auth, db, storage } from '../src/modules/firebase-config.js';
import { getPlanSnapshotStoragePath, getPlanPlantInfoStoragePath, getPlanCalendarStoragePath, getJournalEntryPhotoPath } from '../src/modules/journal-api.js';
import { trackPageView } from '../src/modules/analytics.js';
import { initNavAuth } from '../src/modules/auth-ui.js';
import plantDatabase from '../src/modules/plantDatabase.js';
import { collection, getDocs, getDoc, doc, setDoc, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject, uploadBytes, getDownloadURL } from 'firebase/storage';

// --- PREMIUM GATE ---
let dashboardInitDone = false;
let dashboardStaticUiInitialized = false;
/** Delay (ms) before showing sign-in/upgrade gate so Firebase auth can restore; prevents flash for premium users. */
const GATE_SETTLE_MS = 450;
let gateSettleTimeoutId = null;
const DASHBOARD_MODE_USER = 'user';
const DASHBOARD_MODE_PREVIEW = 'preview';
let currentDashboardMode = DASHBOARD_MODE_USER;
let activeDashboardLoadId = 0;

const PREVIEW_SAMPLE_PLAN_ID = 'sample-summer-kitchen-garden';
const PREVIEW_SAMPLE_YEAR = 2026;
const PREVIEW_YEARS = [2026, 2027];
const PREVIEW_SAMPLE_DATA = {
    plans: [
        {
            id: PREVIEW_SAMPLE_PLAN_ID,
            month: 'June',
            year: PREVIEW_SAMPLE_YEAR,
            title: 'Sample Summer Kitchen Garden',
            plants: ['Eggplant', 'Lettuce', 'Radish', 'Cabbage', 'Tomato'],
            plantCounts: {
                Eggplant: 4,
                Lettuce: 6,
                Radish: 6,
                Cabbage: 2,
                Tomato: 4
            },
            status: 'Active',
            image: '/App Pictures/Sample garden bed.webp',
            plantInfoImage: '/App Pictures/Garden Info.webp',
            calendarImage: '/App Pictures/Garden Calendar.webp'
        }
    ],
    journals: {
        [PREVIEW_SAMPLE_PLAN_ID]: [
            {
                id: 'preview-entry-1',
                date: 'March 10',
                dateSort: `${PREVIEW_SAMPLE_YEAR}-03-10`,
                entry: 'Started tomato and eggplant seeds indoors; sowed radish and lettuce in the cold frame.',
                type: 'Planting',
                icon: 'fa-seedling',
                color: 'bg-emerald-500',
                photoUrl: '/App Pictures/Tomato Seedlings.webp'
            },
            {
                id: 'preview-entry-2',
                date: 'March 13',
                dateSort: `${PREVIEW_SAMPLE_YEAR}-03-13`,
                entry: 'Checked germination trays and rotated seedlings so every tray received even window light.',
                type: 'Observation',
                icon: 'fa-eye',
                color: 'bg-blue-500',
                photoUrl: null
            },
            {
                id: 'preview-entry-3',
                date: 'March 17',
                dateSort: `${PREVIEW_SAMPLE_YEAR}-03-17`,
                entry: 'Mixed fresh compost into the raised bed and loosened the top layer for spring planting.',
                type: 'Soil',
                icon: 'fa-mound',
                color: 'bg-amber-700',
                photoUrl: '/App Pictures/Mixing manure in raised bed.webp'
            },
            {
                id: 'preview-entry-4',
                date: 'March 22',
                dateSort: `${PREVIEW_SAMPLE_YEAR}-03-22`,
                entry: 'Gave the seed starts a diluted fertilizer feed after the first true leaves appeared.',
                type: 'Fertilizer',
                icon: 'fa-flask',
                color: 'bg-green-600',
                photoUrl: null
            },
            {
                id: 'preview-entry-5',
                date: 'March 28',
                dateSort: `${PREVIEW_SAMPLE_YEAR}-03-28`,
                entry: 'Watered the seed trays deeply and set up a simple hardening-off schedule for next month.',
                type: 'Watering',
                icon: 'fa-droplet',
                color: 'bg-cyan-500',
                photoUrl: null
            },
            {
                id: 'preview-entry-6',
                date: 'April 6',
                dateSort: `${PREVIEW_SAMPLE_YEAR}-04-06`,
                entry: 'Finalized the bed layout: eggplant and lettuce on the left, radish block in the middle, cabbage and tomatoes on the right.',
                type: 'Observation',
                icon: 'fa-eye',
                color: 'bg-blue-500',
                photoUrl: '/App Pictures/Garden bed with transplants.webp'
            },
            {
                id: 'preview-entry-7',
                date: 'May 18',
                dateSort: `${PREVIEW_SAMPLE_YEAR}-05-18`,
                entry: 'Transplanted tomatoes and eggplants into the main bed; set out cabbage. Added mulch after a deep watering.',
                type: 'Planting',
                icon: 'fa-seedling',
                color: 'bg-emerald-500',
                photoUrl: null
            },
            {
                id: 'preview-entry-8',
                date: 'July 9',
                dateSort: `${PREVIEW_SAMPLE_YEAR}-07-09`,
                entry: 'First harvest: pulled radishes, cut lettuce and cabbage, and picked ripe tomatoes. Eggplants still sizing up.',
                type: 'Harvest',
                icon: 'fa-basket-shopping',
                color: 'bg-yellow-500',
                photoUrl: '/App Pictures/harvest.webp'
            }
        ]
    },
    seedRows: [
        { type: 'Tomato', variety: 'Roma', amount: '1 packet', year: '2026', shelfLife: '4 years', expiration: '2030', source: 'Flourishing Plants', comments: 'Main sauce bed' },
        { type: 'Basil', variety: 'Genovese', amount: '1 packet', year: '2026', shelfLife: '5 years', expiration: '2031', source: 'Saved seed', comments: 'Pair with tomatoes' },
        { type: 'Lettuce', variety: 'Buttercrunch', amount: '1 packet', year: '2026', shelfLife: '4 years', expiration: '2030', source: 'Local nursery', comments: 'Succession sow every 2 weeks' }
    ],
    budgetRows: [
        { item: 'Seeds', description: 'Tomato, basil, lettuce, marigold', allottedBudget: '25.00', qty: '4', costPerItem: '3.50' },
        { item: 'Compost', description: 'Raised bed top-up', allottedBudget: '40.00', qty: '2', costPerItem: '14.00' },
        { item: 'Mulch', description: 'Straw for tomato bed', allottedBudget: '20.00', qty: '1', costPerItem: '12.00' }
    ],
    harvestRows: [
        { fruitOrVegetable: 'Tomato', variety: 'Roma', weightQty: '6 lbs', harvested: 'July 9' },
        { fruitOrVegetable: 'Basil', variety: 'Genovese', weightQty: '2 bunches', harvested: 'July 9' },
        { fruitOrVegetable: 'Lettuce', variety: 'Buttercrunch', weightQty: '4 heads', harvested: 'June 12' }
    ],
    harvestNotes: [
        { date: `${PREVIEW_SAMPLE_YEAR}-07-09T18:00:00.000Z`, text: 'The first tomato harvest came in a little earlier after using black mulch.' },
        { date: `${PREVIEW_SAMPLE_YEAR}-06-12T10:30:00.000Z`, text: 'Buttercrunch stayed sweet because the bed received afternoon shade.' }
    ]
};

function isPreviewMode() {
    return currentDashboardMode === DASHBOARD_MODE_PREVIEW;
}

function showGateLoading() {
    const loading = document.getElementById('dashboard-gate-loading');
    const signin = document.getElementById('dashboard-gate-signin');
    const upgrade = document.getElementById('dashboard-gate-upgrade');
    const gate = document.getElementById('dashboard-gate');
    const app = document.getElementById('dashboard-app');
    if (loading) loading.classList.remove('hidden');
    if (signin) signin.classList.add('hidden');
    if (upgrade) upgrade.classList.add('hidden');
    if (gate) gate.classList.remove('hidden');
    if (app) app.classList.add('hidden');
    document.body.classList.remove('dashboard-preview-mode');
}

function showGateSignIn() {
    const loading = document.getElementById('dashboard-gate-loading');
    const signin = document.getElementById('dashboard-gate-signin');
    const upgrade = document.getElementById('dashboard-gate-upgrade');
    const gate = document.getElementById('dashboard-gate');
    const app = document.getElementById('dashboard-app');
    if (loading) loading.classList.add('hidden');
    if (signin) signin.classList.remove('hidden');
    if (upgrade) upgrade.classList.add('hidden');
    if (gate) gate.classList.remove('hidden');
    if (app) app.classList.add('hidden');
    document.body.classList.remove('dashboard-preview-mode');
}

function showGateUpgrade() {
    const loading = document.getElementById('dashboard-gate-loading');
    const signin = document.getElementById('dashboard-gate-signin');
    const upgrade = document.getElementById('dashboard-gate-upgrade');
    const gate = document.getElementById('dashboard-gate');
    const app = document.getElementById('dashboard-app');
    if (loading) loading.classList.add('hidden');
    if (signin) signin.classList.add('hidden');
    if (upgrade) upgrade.classList.remove('hidden');
    if (gate) gate.classList.remove('hidden');
    if (app) app.classList.add('hidden');
    document.body.classList.remove('dashboard-preview-mode');
}

function showDashboard(mode = DASHBOARD_MODE_USER) {
    currentDashboardMode = mode;
    const gate = document.getElementById('dashboard-gate');
    const app = document.getElementById('dashboard-app');
    if (gate) gate.classList.add('hidden');
    if (app) app.classList.remove('hidden');
    updateDashboardUserDisplay(auth?.currentUser || null);
    if (!dashboardInitDone) {
        dashboardInitDone = true;
        init();
        return;
    }
    // Unlock/lock inputs immediately when mode changes so plant search etc. are correct before async load
    applyDashboardModeInteractivity();
    configurePlantCardSearchForCurrentMode();
    void loadDashboardForCurrentMode();
}

/** Update header to show signed-in user name; avatar is always a person icon. */
function updateDashboardUserDisplay(user) {
    const nameEl = document.getElementById('dashboard-user-name');
    const labelEl = document.getElementById('dashboard-subscription-label');
    const badgeEl = document.getElementById('dashboard-subscription-badge');
    const bannerEl = document.getElementById('dashboard-mode-banner');
    const displayName = (user?.displayName && user.displayName.trim()) ? user.displayName.trim() : (user?.email ? user.email.split('@')[0] : null);

    if (nameEl) {
        nameEl.textContent = isPreviewMode() ? 'Preview Visitor' : (displayName || 'User');
    }

    if (labelEl) {
        labelEl.textContent = isPreviewMode() ? 'Mode' : 'Subscription';
    }

    if (badgeEl) {
        badgeEl.className = isPreviewMode() ? 'dashboard-badge-active dashboard-badge-preview' : 'dashboard-badge-active';
        badgeEl.innerHTML = isPreviewMode()
            ? '<i class="fa-solid fa-eye"></i> Preview'
            : '<i class="fa-solid fa-check-circle"></i> Active';
    }

    if (bannerEl) {
        if (isPreviewMode()) {
            bannerEl.innerHTML = `
                <div class="dashboard-mode-banner-content">
                    <div>
                        <p class="dashboard-mode-banner-title">Preview Mode</p>
                        <p class="dashboard-mode-banner-text">You are viewing a sample Journal. Sign in and upgrade to create and save your own garden data.</p>
                    </div>
                    <div class="dashboard-mode-banner-cta">
                        <p class="dashboard-mode-banner-price">Limited-time offer: $14.99</p>
                        <a href="https://app.flourishingplants.com/Landing/landing.html#pricing" target="_blank" rel="noopener noreferrer" class="upgrade-premium-btn" aria-label="Upgrade to Premium for full access">
                            <span>See Pricing Options</span>
                        </a>
                    </div>
                </div>
            `;
            bannerEl.classList.remove('hidden');
        } else {
            bannerEl.innerHTML = '';
            bannerEl.classList.add('hidden');
        }
    }

    document.body.classList.toggle('dashboard-preview-mode', isPreviewMode());
}

function setupPremiumGate() {
    initializeSubscription();
    subscriptionManager.onSubscriptionChange((state) => {
        if (state.isPremium) {
            if (gateSettleTimeoutId) {
                clearTimeout(gateSettleTimeoutId);
                gateSettleTimeoutId = null;
            }
            showDashboard(DASHBOARD_MODE_USER);
        } else if (state.isLoggedIn) {
            if (gateSettleTimeoutId) {
                clearTimeout(gateSettleTimeoutId);
                gateSettleTimeoutId = null;
            }
            // Signed-in non-premium users see the read-only preview dashboard
            showDashboard(DASHBOARD_MODE_PREVIEW);
        } else {
            // Stay on "Checking access…" briefly so auth can restore; avoids flash of preview for premium users
            if (gateSettleTimeoutId) return;
            gateSettleTimeoutId = setTimeout(() => {
                gateSettleTimeoutId = null;
                showDashboard(DASHBOARD_MODE_PREVIEW);
            }, GATE_SETTLE_MS);
        }
    });
}

// --- DATA (loaded from Firestore) ---
const gardenData = {};
const journals = {};
const loadedJournalPlanIds = new Set();
const journalLoadPromises = new Map();
let calendarPreloadPromise = null;
const JOURNAL_CACHE_KEY_PREFIX = 'flourishing-dashboard-journals';

// --- UI STATE ---
let currentYear = new Date().getFullYear();
let currentJournalPlanId = null;
/** When viewing a plan opened from Garden Plan Overview link, holds the overview plan id for "Go to Garden Plan Overview" button. */
let openedFromGardenPlanOverviewPlanId = null;
/** Years added via "Add year" (e.g. 2028, 2029) so they stay in the row. */
let extraUpcomingYears = new Set();
/** Years removed from the row by the user (can re-add via + for future years). */
let removedFromYearRow = new Set();

function resetDashboardDataState() {
    Object.keys(gardenData).forEach((year) => delete gardenData[year]);
    Object.keys(journals).forEach((planId) => delete journals[planId]);
    loadedJournalPlanIds.clear();
    journalLoadPromises.clear();
    calendarPreloadPromise = null;
    currentJournalPlanId = null;
    currentJournalPlanYear = null;
}

function loadPreviewGardenPlans() {
    resetDashboardDataState();
    currentYear = PREVIEW_SAMPLE_YEAR;
    extraUpcomingYears = new Set();
    removedFromYearRow = new Set();
    gardenData[PREVIEW_SAMPLE_YEAR] = PREVIEW_SAMPLE_DATA.plans.map((plan) => ({
        ...plan,
        plants: Array.isArray(plan.plants) ? [...plan.plants] : [],
        ...(plan.plantCounts && typeof plan.plantCounts === 'object' ? { plantCounts: { ...plan.plantCounts } } : {}),
        ...(plan.plantBaseByDisplay && typeof plan.plantBaseByDisplay === 'object'
            ? { plantBaseByDisplay: { ...plan.plantBaseByDisplay } }
            : {})
    }));
    Object.entries(PREVIEW_SAMPLE_DATA.journals).forEach(([planId, entries]) => {
        journals[planId] = entries.map((entry) => ({ ...entry }));
        loadedJournalPlanIds.add(planId);
    });
}

async function loadGardenPlans() {
    const user = auth?.currentUser;
    const uid = user?.uid;
    if (!uid || !db) return;
    const col = collection(db, 'users', uid, 'gardenPlans');
    const snap = await getDocs(col);
    // Keep user doc identifiable by email in Firestore console (document ID stays UID)
    if (user?.email) {
        const userRef = doc(db, 'users', uid);
        await setDoc(userRef, { email: user.email, updatedAt: new Date().toISOString() }, { merge: true });
    }
    Object.keys(gardenData).forEach(y => delete gardenData[y]);
    snap.docs.forEach(d => {
        const data = d.data();
        const year = data.year || new Date().getFullYear();
        if (!gardenData[year]) gardenData[year] = [];
        gardenData[year].push({
            id: d.id,
            month: data.month || '',
            year: data.year,
            title: data.title || '',
            plants: Array.isArray(data.plants) ? data.plants : [],
            plantCounts: data.plantCounts && typeof data.plantCounts === 'object' ? { ...data.plantCounts } : null,
            plantBaseByDisplay:
                data.plantBaseByDisplay && typeof data.plantBaseByDisplay === 'object'
                    ? { ...data.plantBaseByDisplay }
                    : null,
            status: data.status || 'Active',
            image: data.image || null,
            plantInfoImage: data.plantInfoImage || null,
            calendarImage: data.calendarImage || null,
            createdAt: data.createdAt != null ? data.createdAt : null
        });
    });
    // Ensure current year key exists
    if (!gardenData[currentYear]) gardenData[currentYear] = [];
    pruneJournalStateForCurrentPlans();
}

function getJournalCacheKey(uid) {
    return `${JOURNAL_CACHE_KEY_PREFIX}:${uid}`;
}

function persistJournalCache() {
    if (isPreviewMode()) return;
    const uid = auth?.currentUser?.uid;
    if (!uid) return;
    try {
        const cachedJournals = {};
        getAllPlanIds().forEach((planId) => {
            if (Array.isArray(journals[planId])) cachedJournals[planId] = journals[planId];
        });
        sessionStorage.setItem(getJournalCacheKey(uid), JSON.stringify({
            savedAt: Date.now(),
            journals: cachedJournals
        }));
    } catch (_) {
        // Ignore storage quota and private mode issues.
    }
}

function restoreJournalCache() {
    if (isPreviewMode()) return false;
    const uid = auth?.currentUser?.uid;
    if (!uid) return false;
    try {
        const raw = sessionStorage.getItem(getJournalCacheKey(uid));
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        const validPlanIds = new Set(getAllPlanIds());
        let restoredAny = false;
        Object.entries(parsed?.journals || {}).forEach(([planId, entries]) => {
            if (!validPlanIds.has(planId) || !Array.isArray(entries)) return;
            journals[planId] = entries;
            loadedJournalPlanIds.add(planId);
            restoredAny = true;
        });
        return restoredAny;
    } catch (_) {
        return false;
    }
}

function pruneJournalStateForCurrentPlans() {
    const validPlanIds = new Set(getAllPlanIds());
    Object.keys(journals).forEach((planId) => {
        if (validPlanIds.has(planId)) return;
        delete journals[planId];
        loadedJournalPlanIds.delete(planId);
        journalLoadPromises.delete(planId);
    });
}

/** Baseline: from 2025 through current calendar year only; later years added one at a time via "+". */
const YEAR_ROW_START = 2025;

function renderYearRow() {
    if (isPreviewMode()) {
        const row = document.querySelector('.dashboard-year-row');
        if (!row) return;
        row.innerHTML = PREVIEW_YEARS.map((year) => `
            <span class="year-btn-wrapper">
                <button type="button" onclick="filterYear(event, ${year})" class="year-btn ${year === currentYear ? 'year-btn-active' : ''}" data-year="${year}">${year}</button>
            </span>
        `).join('');
        return;
    }
    const thisYear = new Date().getFullYear();
    const fromData = Object.keys(gardenData).map(Number);
    const endYear = thisYear;
    const alwaysUpcoming = [];
    for (let y = YEAR_ROW_START; y <= endYear; y++) alwaysUpcoming.push(y);
    const years = [...new Set([currentYear, ...fromData, ...alwaysUpcoming, ...extraUpcomingYears])]
        .filter(y => y >= YEAR_ROW_START && !removedFromYearRow.has(y))
        .sort((a, b) => a - b)
        .slice(0, 12);
    const row = document.querySelector('.dashboard-year-row');
    if (!row) return;
    row.innerHTML = years.map(y => `
        <span class="year-btn-wrapper">
            <button type="button" onclick="filterYear(event, ${y})" class="year-btn ${y === currentYear ? 'year-btn-active' : ''}" data-year="${y}">${y}</button>
            <button type="button" onclick="event.stopPropagation(); confirmRemoveYear(${y})" class="year-btn-delete" title="Remove year from list" aria-label="Remove year ${y}">
                <i class="fa-solid fa-xmark"></i>
            </button>
        </span>
    `).join('') + `
        <button type="button" onclick="addUpcomingYear()" class="year-btn year-btn-add" title="Add upcoming year" aria-label="Add upcoming year">
            <i class="fa-solid fa-plus"></i>
        </button>`;
}

function addUpcomingYear() {
    if (isPreviewMode()) return;
    const row = document.querySelector('.dashboard-year-row');
    const yearBtns = row ? row.querySelectorAll('.year-btn[data-year]') : [];
    const maxYear = yearBtns.length
        ? Math.max(...Array.from(yearBtns).map(b => parseInt(b.dataset.year, 10)))
        : new Date().getFullYear();
    const nextYear = maxYear + 1;
    extraUpcomingYears.add(nextYear);
    removedFromYearRow.delete(nextYear);
    currentYear = nextYear;
    renderYearRow();
    renderPlans();
}

let pendingRemoveYear = null;

function confirmRemoveYear(year) {
    if (isPreviewMode()) return;
    const visibleYears = getVisibleYears();
    if (visibleYears.length <= 1) return;
    pendingRemoveYear = year;
    const msg = document.getElementById('remove-year-modal-message');
    if (msg) msg.textContent = `Remove ${year} from the year list? Archived plans for this year are not deleted.`;
    const modal = document.getElementById('remove-year-modal');
    if (modal) modal.classList.add('show');
}

function getVisibleYears() {
    const thisYear = new Date().getFullYear();
    const fromData = Object.keys(gardenData).map(Number);
    const endYear = thisYear;
    const alwaysUpcoming = [];
    for (let y = YEAR_ROW_START; y <= endYear; y++) alwaysUpcoming.push(y);
    return [...new Set([currentYear, ...fromData, ...alwaysUpcoming, ...extraUpcomingYears])]
        .filter(y => y >= YEAR_ROW_START && !removedFromYearRow.has(y))
        .sort((a, b) => a - b)
        .slice(0, 12);
}

function closeRemoveYearModal() {
    pendingRemoveYear = null;
    const modal = document.getElementById('remove-year-modal');
    if (modal) modal.classList.remove('show');
}

function removeYear() {
    if (isPreviewMode()) return;
    if (pendingRemoveYear == null) return;
    const year = pendingRemoveYear;
    pendingRemoveYear = null;
    removedFromYearRow.add(year);
    extraUpcomingYears.delete(year);
    if (currentYear === year) {
        const visible = getVisibleYears().filter(y => y !== year);
        currentYear = visible.length ? visible[0] : new Date().getFullYear();
    }
    closeRemoveYearModal();
    renderYearRow();
    renderPlans();
}

let pendingRemovePlanId = null;

function confirmRemovePlan(planId) {
    if (isPreviewMode()) return;
    pendingRemovePlanId = planId;
    const modal = document.getElementById('remove-plan-modal');
    if (modal) modal.classList.add('show');
}

function closeRemovePlanModal() {
    pendingRemovePlanId = null;
    const modal = document.getElementById('remove-plan-modal');
    if (modal) modal.classList.remove('show');
}

async function loadJournalEntries(planId, options = {}) {
    const { force = false } = options;
    if (isPreviewMode()) {
        return Array.isArray(journals[planId]) ? journals[planId] : [];
    }
    const uid = auth?.currentUser?.uid;
    if (!uid || !db || !planId) return [];
    if (!force && loadedJournalPlanIds.has(planId) && Array.isArray(journals[planId])) {
        return journals[planId];
    }
    if (journalLoadPromises.has(planId)) return journalLoadPromises.get(planId);
    const col = collection(db, 'users', uid, 'gardenPlans', planId, 'journalEntries');
    const loadPromise = getDocs(col).then((snap) => {
        const planYear = getPlanYear(planId);
        const entries = snap.docs.map(d => {
            const data = d.data();
            return { id: d.id, date: data.date, dateSort: data.dateSort, entry: data.entry, type: data.type || 'Note', icon: data.icon, color: data.color, photoUrl: data.photoUrl || null };
        });
        entries.sort((a, b) => getEntrySortKey(b, planYear).localeCompare(getEntrySortKey(a, planYear)));
        journals[planId] = entries;
        loadedJournalPlanIds.add(planId);
        persistJournalCache();
        return entries;
    }).finally(() => {
        journalLoadPromises.delete(planId);
    });
    journalLoadPromises.set(planId, loadPromise);
    return loadPromise;
}

const entryTypeConfig = {
    Planting:   { icon: 'fa-seedling',        color: 'bg-emerald-500', iconColor: '#10b981', bgLight: 'dashboard-entry-bg-emerald' },
    Observation: { icon: 'fa-eye',            color: 'bg-blue-500', iconColor: '#3b82f6', bgLight: 'dashboard-entry-bg-blue' },
    Harvest:    { icon: 'fa-basket-shopping', color: 'bg-yellow-500', iconColor: '#f59e0b', bgLight: 'dashboard-entry-bg-amber' },
    Note:       { icon: 'fa-note-sticky',     color: 'bg-slate-500', iconColor: '#64748b', bgLight: 'dashboard-entry-bg-slate' },
    Weather:    { icon: 'fa-cloud-sun',       color: 'bg-sky-500', iconColor: '#0ea5e9', bgLight: 'dashboard-entry-bg-sky' },
    Soil:       { icon: 'fa-mound',           color: 'bg-amber-700', iconColor: '#b45309', bgLight: 'dashboard-entry-bg-amber' },
    Fertilizer: { icon: 'fa-flask',           color: 'bg-green-600', iconColor: '#16a34a', bgLight: 'dashboard-entry-bg-green' },
    Watering:   { icon: 'fa-droplet',         color: 'bg-cyan-500', iconColor: '#06b6d4', bgLight: 'dashboard-entry-bg-cyan' },
    Pests:      { icon: 'fa-bug',             color: 'bg-orange-500', iconColor: '#f97316', bgLight: 'dashboard-entry-bg-amber' }
};

const PLANT_CARD_MONTH_LABELS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const PLANT_CARD_MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/* Filenames must match repo files exactly — Linux / Firebase Hosting is case-sensitive (Windows dev is not). */
const PLANT_CARD_IMAGE_NAME_MAP = {
    'Beets': 'Beet.webp',
    'Broad Beans': 'Broad bean.webp',
    'Carrots': 'Carrot.webp',
    'Leeks': 'Leek.webp',
    'Onions': 'Onion.webp',
    'Beans': 'Beans.webp',
    'Potatoes': 'Potato.webp',
    'Fennel': 'Fennel (2).webp',
    'Bok Choy': 'Bok Choy.webp',
    'Pak Choi': 'Bok Choy.webp',
    'Tomato': 'tomato.webp',
    'Tomatoes': 'tomato.webp',
    'Peppers': 'Bell pepper.webp',
    'Bell Pepper': 'Bell pepper.webp',
    'Bell Peppers': 'Bell pepper.webp',
    'Hot Pepper': 'Hot Pepper.webp',
    'Hot Peppers': 'Hot Pepper.webp',
    'Chili Peppers': 'Chilli Pepper.webp',
    'Chilli Peppers': 'Chilli Pepper.webp',
    'Green Beans': 'green beans.webp',
    'Snow Peas': 'Snap peas.webp',
    'Sugar Snap Peas': 'Sugar Snap peas.webp',
    'Swiss Chard': 'Swiss Chard.webp',
    'Brussel Sprout': 'Brussel Sprout.webp',
    'Brussels Sprouts': 'Brussel Sprout.webp'
};
const PLANT_CARD_NAMES = Object.keys(plantDatabase).sort((a, b) => a.localeCompare(b));
const PREVIEW_FIXED_PLANT_CARD = plantDatabase.Tomato ? 'Tomato' : PLANT_CARD_NAMES[0];
let activePlantCardName = null;

function getPlantCardImageFileName(plantName) {
    return PLANT_CARD_IMAGE_NAME_MAP[plantName] || `${plantName}.webp`;
}

/** Case-only variant for Linux hosts: e.g. Tomato.webp → tomato.webp (not used for multi-word filenames). */
function getPlantCardImageVegLowercaseUrl(fileName) {
    const base = fileName.replace(/\.webp$/i, '');
    if (!base || base.includes(' ') || base === base.toLowerCase()) return null;
    return `/src/assets/Vegetable Pictures/${encodeURIComponent(`${base.toLowerCase()}.webp`)}`;
}

function getPlantCardImageSources(plantName) {
    const fileName = getPlantCardImageFileName(plantName);
    // Use root-relative paths so images work on live host (relative paths break with rewrites/SPA)
    const vegLower = getPlantCardImageVegLowercaseUrl(fileName);
    return {
        primary: `/src/assets/Vegetable Pictures/${encodeURIComponent(fileName)}`,
        vegLowercase: vegLower,
        fallback: `/src/assets/Flower Pictures/${encodeURIComponent(fileName.toLowerCase())}`
    };
}

function formatPlantsPerSquareFoot(value) {
    if (value == null || value === '') return 'N/A';
    return `${value} / Sq Ft`;
}

function formatDaysToMaturity(value) {
    if (value == null || value === '') return 'N/A';
    return String(value).includes('day') || String(value).includes('Day') ? String(value) : `${value} Days`;
}

function getPlantCardSearchMatches(query) {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return [];
    return PLANT_CARD_NAMES.filter(name => name.toLowerCase().startsWith(q)).slice(0, 12);
}

function renderPlantCardSuggestions(matches) {
    const resultsEl = document.getElementById('plant-card-search-results');
    const inputEl = document.getElementById('plant-card-search');
    if (!resultsEl || !inputEl) return;
    if (!matches.length) {
        const query = inputEl.value.trim();
        if (!query) {
            resultsEl.innerHTML = '';
            resultsEl.classList.add('hidden');
            inputEl.setAttribute('aria-expanded', 'false');
            return;
        }
        resultsEl.innerHTML = '<div class="dashboard-plant-card-search-option" aria-disabled="true">No plants found</div>';
        resultsEl.classList.remove('hidden');
        inputEl.setAttribute('aria-expanded', 'true');
        return;
    }
    resultsEl.innerHTML = matches.map(name => `
        <button type="button" class="dashboard-plant-card-search-option" role="option" data-plant-name="${escapeHtml(name)}">
            ${escapeHtml(name)}
        </button>
    `).join('');
    resultsEl.classList.remove('hidden');
    inputEl.setAttribute('aria-expanded', 'true');
}

function closePlantCardSuggestions() {
    const resultsEl = document.getElementById('plant-card-search-results');
    const inputEl = document.getElementById('plant-card-search');
    if (resultsEl) {
        resultsEl.innerHTML = '';
        resultsEl.classList.add('hidden');
    }
    if (inputEl) inputEl.setAttribute('aria-expanded', 'false');
}

function buildPlantCalendarGrid(plant) {
    return PLANT_CARD_MONTH_LABELS.map((label, idx) => {
        const month = idx + 1;
        const classes = [];
        if (Array.isArray(plant.sow) && plant.sow.includes(month)) classes.push('sow');
        if (Array.isArray(plant.plant) && plant.plant.includes(month)) classes.push('plant');
        if (Array.isArray(plant.harvest) && plant.harvest.includes(month)) classes.push('harvest');
        const stateClass = classes.length ? `${classes.join(' ')} active` : 'inactive';
        return `
            <div class="dashboard-plant-card-calendar-month" title="${PLANT_CARD_MONTH_NAMES[idx]}">
                <div class="dashboard-plant-card-calendar-bars">
                    <span class="dashboard-plant-card-calendar-bar ${stateClass}"></span>
                </div>
                <span class="dashboard-plant-card-calendar-label">${label}</span>
            </div>
        `;
    }).join('');
}

function getPlantCardImageMarkup(plantName) {
    const sources = getPlantCardImageSources(plantName);
    const vegLowerAttr = sources.vegLowercase
        ? ` data-fallback-veg-lower="${escapeHtml(sources.vegLowercase)}"`
        : '';
    return `
        <div class="dashboard-plant-card-image">
            <img
                src="${escapeHtml(sources.primary)}"
                ${vegLowerAttr}
                data-fallback-src="${escapeHtml(sources.fallback)}"
                alt="${escapeHtml(plantName)}"
                loading="lazy"
            />
        </div>
    `;
}

function applyPlantCardImageFallbacks() {
    document.querySelectorAll('.dashboard-plant-card-image img').forEach((img) => {
        img.onerror = () => {
            const vegLower = img.getAttribute('data-fallback-veg-lower');
            if (vegLower && img.getAttribute('src') !== vegLower) {
                img.removeAttribute('data-fallback-veg-lower');
                img.setAttribute('src', vegLower);
                return;
            }
            const fallback = img.getAttribute('data-fallback-src');
            if (fallback && img.getAttribute('src') !== fallback) {
                img.setAttribute('src', fallback);
                return;
            }
            img.style.display = 'none';
        };
    });
}

function renderPlantCard(plantName) {
    const cardEl = document.getElementById('dashboard-plant-card');
    if (!cardEl) return;
    const plant = plantDatabase[plantName];
    if (!plant) {
        cardEl.innerHTML = '<div class="dashboard-plant-card-empty">Search for a plant to view its growing guide.</div>';
        return;
    }
    activePlantCardName = plantName;
    const whenToPlant = plant.whenToPlant || 'N/A';
    const notes = plant.notes || 'No additional grower notes available.';
    cardEl.innerHTML = `
        <div class="dashboard-plant-card-header">
            ${getPlantCardImageMarkup(plantName)}
            <div class="dashboard-plant-card-header-main">
                <div class="dashboard-plant-card-heading">
                    <h3>${escapeHtml(plantName)}</h3>
                    <p class="dashboard-plant-card-subtitle">${escapeHtml(whenToPlant)}</p>
                </div>
                <div class="dashboard-plant-card-stat-row dashboard-plant-card-stat-row-inline">
                    <div class="dashboard-plant-card-stat">
                        <p class="dashboard-plant-card-stat-label">Maturity</p>
                        <p class="dashboard-plant-card-stat-value">${escapeHtml(formatDaysToMaturity(plant.daysToMaturity))}</p>
                    </div>
                    <div class="dashboard-plant-card-stat">
                        <p class="dashboard-plant-card-stat-label">SFG</p>
                        <p class="dashboard-plant-card-stat-value">${escapeHtml(formatPlantsPerSquareFoot(plant.plantsPerSquareFoot))}</p>
                    </div>
                    <div class="dashboard-plant-card-stat">
                        <p class="dashboard-plant-card-stat-label">Height</p>
                        <p class="dashboard-plant-card-stat-value">${escapeHtml(plant.height || 'N/A')}</p>
                    </div>
                </div>
            </div>
        </div>

        <div class="dashboard-plant-card-detail-grid">
            <div class="dashboard-plant-card-detail sunlight">
                <div class="dashboard-plant-card-detail-icon"><i class="fa-regular fa-sun"></i></div>
                <p class="dashboard-plant-card-stat-label">Sunlight</p>
                <p class="dashboard-plant-card-detail-value">${escapeHtml(plant.sun || 'N/A')}</p>
            </div>
            <div class="dashboard-plant-card-detail watering">
                <div class="dashboard-plant-card-detail-icon"><i class="fa-solid fa-water"></i></div>
                <p class="dashboard-plant-card-stat-label">Watering</p>
                <p class="dashboard-plant-card-detail-value">${escapeHtml(plant.water || 'N/A')}</p>
            </div>
            <div class="dashboard-plant-card-detail soil-temp">
                <div class="dashboard-plant-card-detail-icon"><i class="fa-solid fa-temperature-three-quarters"></i></div>
                <p class="dashboard-plant-card-stat-label">Soil Temp</p>
                <p class="dashboard-plant-card-detail-value">${escapeHtml(plant.soilTemperature || 'N/A')}</p>
            </div>
            <div class="dashboard-plant-card-detail depth">
                <div class="dashboard-plant-card-detail-icon"><i class="fa-solid fa-arrow-down-long"></i></div>
                <p class="dashboard-plant-card-stat-label">Sowing Depth</p>
                <p class="dashboard-plant-card-detail-value">${escapeHtml(plant.depth || 'N/A')}</p>
            </div>
        </div>

        <div class="dashboard-plant-card-meta-grid">
            <div class="dashboard-plant-card-meta dashboard-plant-card-meta-spacing">
                <div class="dashboard-plant-card-meta-icon"><i class="fa-solid fa-ruler-horizontal" aria-hidden="true"></i></div>
                <p class="dashboard-plant-card-stat-label">Plant Spacing</p>
                <p class="dashboard-plant-card-meta-value">${escapeHtml(plant.spacing || 'N/A')}</p>
            </div>
            <div class="dashboard-plant-card-meta dashboard-plant-card-meta-pests">
                <div class="dashboard-plant-card-meta-icon"><i class="fa-solid fa-bug" aria-hidden="true"></i></div>
                <p class="dashboard-plant-card-stat-label">Common Pests</p>
                <p class="dashboard-plant-card-meta-value">${escapeHtml(plant.pests || 'N/A')}</p>
            </div>
        </div>

        <div class="dashboard-plant-card-calendar dashboard-plant-card-calendar-panel">
            <div class="dashboard-plant-card-calendar-header">
                <p class="dashboard-plant-card-section-label">Growth Calendar</p>
                <div class="dashboard-plant-card-calendar-legend">
                    <span><i class="fa-solid fa-circle" style="color:#eab308"></i> Sow</span>
                    <span><i class="fa-solid fa-circle" style="color:#22c55e"></i> Plant</span>
                    <span><i class="fa-solid fa-circle" style="color:#ef4444"></i> Harvest</span>
                </div>
            </div>
            <div class="dashboard-plant-card-calendar-grid">
                ${buildPlantCalendarGrid(plant)}
            </div>
        </div>

        <div class="dashboard-plant-card-panel">
            <p class="dashboard-plant-card-section-label">Good Companions</p>
            <p class="dashboard-plant-card-panel-text">${escapeHtml(plant.companions || 'N/A')}</p>
        </div>

        <div class="dashboard-plant-card-panel dashboard-plant-card-panel-notes">
            <p class="dashboard-plant-card-section-label">Grower's Notes:</p>
            <p class="dashboard-plant-card-panel-text dashboard-plant-card-notes">${escapeHtml(notes)}</p>
        </div>
    `;
    applyPlantCardImageFallbacks();
}

function selectPlantCard(plantName) {
    if (isPreviewMode()) return;
    const inputEl = document.getElementById('plant-card-search');
    if (inputEl) inputEl.value = plantName;
    closePlantCardSuggestions();
    if (inputEl) inputEl.blur();
    renderPlantCard(plantName);
}

function initPlantCardSearch() {
    const inputEl = document.getElementById('plant-card-search');
    const resultsEl = document.getElementById('plant-card-search-results');
    if (!inputEl || !resultsEl) return;

    inputEl.addEventListener('input', () => {
        if (isPreviewMode()) return;
        renderPlantCardSuggestions(getPlantCardSearchMatches(inputEl.value));
    });

    inputEl.addEventListener('focus', () => {
        if (isPreviewMode()) return;
        if (inputEl.value.trim()) renderPlantCardSuggestions(getPlantCardSearchMatches(inputEl.value));
    });

    inputEl.addEventListener('keydown', (e) => {
        if (isPreviewMode()) return;
        if (e.key === 'Escape') {
            closePlantCardSuggestions();
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            const firstMatch = getPlantCardSearchMatches(inputEl.value)[0];
            if (firstMatch) selectPlantCard(firstMatch);
        }
    });

    resultsEl.addEventListener('click', (e) => {
        if (isPreviewMode()) return;
        const btn = e.target.closest('[data-plant-name]');
        if (!btn) return;
        selectPlantCard(btn.getAttribute('data-plant-name') || '');
    });

    document.addEventListener('click', (e) => {
        if (e.target === inputEl || inputEl.contains(e.target) || resultsEl.contains(e.target)) return;
        closePlantCardSuggestions();
    });

    renderPlantCard(PREVIEW_FIXED_PLANT_CARD);
}

function configurePlantCardSearchForCurrentMode() {
    const inputEl = document.getElementById('plant-card-search');
    if (!inputEl) return;

    closePlantCardSuggestions();
    // Use subscription manager as source of truth so premium users always get search even if mode is out of sync
    const subscriptionInfo = subscriptionManager.getSubscriptionInfo?.() ?? {};
    const isPremium = subscriptionInfo.isPremium === true;
    if (!isPremium) {
        inputEl.value = '';
        inputEl.disabled = true;
        inputEl.readOnly = true;
        inputEl.placeholder = 'Plant search unlocks after you sign in and upgrade.';
        renderPlantCard(PREVIEW_FIXED_PLANT_CARD);
        return;
    }

    inputEl.disabled = false;
    inputEl.readOnly = false;
    inputEl.placeholder = 'Start typing a plant name...';
    renderPlantCard(activePlantCardName || PREVIEW_FIXED_PLANT_CARD);
}

/** Tab ids that map to `location.hash` (e.g. /journal#plant-card). */
const DASHBOARD_TAB_HASH_IDS = ['archive', 'calendar', 'plant-card', 'seeds', 'harvest', 'budget'];
const DASHBOARD_TAB_HASH_SET = new Set(DASHBOARD_TAB_HASH_IDS);

function getDashboardTabIdFromHash() {
    const raw = (location.hash || '').replace(/^#/, '').trim();
    if (!raw) return null;
    return DASHBOARD_TAB_HASH_SET.has(raw) ? raw : null;
}

function applyDashboardTabFromLocationHash() {
    const tab = getDashboardTabIdFromHash();
    if (!tab) return;
    showTab(tab);
}

function showTab(tabId) {
    document.querySelectorAll('button[id^="tab-"]').forEach(btn => {
        btn.classList.remove('tab-active');
        btn.classList.add('text-slate-500', 'border-transparent');
    });
    const activeTab = document.getElementById(`tab-${tabId}`);
    if (activeTab) {
        activeTab.classList.add('tab-active');
        activeTab.classList.remove('text-slate-500', 'border-transparent');
    }
    document.querySelectorAll('.tab-view').forEach(view => view.classList.add('hidden'));
    const view = document.getElementById(`view-${tabId}`);
    if (view) view.classList.remove('hidden');
    if (tabId === 'archive') closeJournal();
    if (tabId === 'calendar') onShowCalendarTab();
    if (tabId === 'plant-card') configurePlantCardSearchForCurrentMode();
    if (view && DASHBOARD_TAB_HASH_SET.has(tabId)) {
        const next = `#${tabId}`;
        if (location.hash !== next) {
            history.replaceState(null, '', next);
        }
    }
}

function filterYear(ev, year) {
    currentYear = year;
    document.querySelectorAll('.year-btn').forEach(btn => {
        btn.classList.remove('year-btn-active');
        btn.classList.add('text-slate-500');
    });
    const target = ev && ev.target;
    if (target) {
        target.classList.add('year-btn-active');
        target.classList.remove('text-slate-500');
    }
    renderPlans();
}

/** Milliseconds since epoch for plan.createdAt (ISO string or Firestore Timestamp); 0 if missing. */
function getPlanCreatedAtMs(plan) {
    const c = plan?.createdAt;
    if (c == null) return 0;
    if (typeof c === 'object' && typeof c.toDate === 'function') {
        try {
            const d = c.toDate();
            const ms = d.getTime();
            return Number.isFinite(ms) ? ms : 0;
        } catch (_) {
            return 0;
        }
    }
    if (typeof c === 'number' && Number.isFinite(c)) return c;
    const t = Date.parse(String(c));
    return Number.isFinite(t) ? t : 0;
}

function renderPlans() {
    const grid = document.getElementById('plans-grid');
    if (!grid) return;
    const rawPlans = gardenData[currentYear] || [];
    // Garden Plan Overview first; then bed plans newest-first by createdAt
    const plans = rawPlans.slice().sort((a, b) => {
        const aIsOverview = a.title === 'Garden Plan Overview' ? 0 : 1;
        const bIsOverview = b.title === 'Garden Plan Overview' ? 0 : 1;
        if (aIsOverview !== bIsOverview) return aIsOverview - bIsOverview;
        const tb = getPlanCreatedAtMs(b);
        const ta = getPlanCreatedAtMs(a);
        if (tb !== ta) return tb - ta;
        return String(a.title || '').localeCompare(String(b.title || ''), undefined, { sensitivity: 'base' });
    });
    if (!plans.length && !isPreviewMode()) {
        grid.innerHTML = `
            <div class="dashboard-empty-state">
                <div class="dashboard-empty-state-icon"><i class="fa-solid fa-seedling"></i></div>
                <h3>Create your first garden plan</h3>
                <p>Your dashboard is empty right now. Start in the Garden Planner and your plans, journal activity, and saved records will appear here.</p>
                <a href="https://app.flourishingplants.com/" class="dashboard-empty-state-link">Go to Garden Planner</a>
            </div>
        `;
        return;
    }

    const cardsHtml = plans.map((plan) => {
        const seenTags = new Set();
        const tagNames = [];
        for (const p of plan.plants || []) {
            const key = String(p).trim();
            if (!key || seenTags.has(key)) continue;
            seenTags.add(key);
            tagNames.push(key);
        }
        return `
        <div class="dashboard-plan-card" data-plan-id="${escapeHtml(plan.id)}">
            <div class="dashboard-flex dashboard-flex-between" style="margin-bottom: 0.75rem;">
                <span class="dashboard-plan-meta">${escapeHtml(plan.month)} ${plan.year}</span>
                <div class="dashboard-flex dashboard-flex-center dashboard-gap-2">
                    <div class="dashboard-plan-status-dot ${plan.status === 'Active' ? 'active' : 'inactive'}"></div>
                    <button type="button" onclick="confirmRemovePlan('${escapeHtml(plan.id)}')" class="dashboard-plan-remove" title="Remove plan" aria-label="Remove plan">
                        <i class="fa-solid fa-trash-can"></i>
                    </button>
                </div>
            </div>
            <h4 class="dashboard-plan-title">${escapeHtml(plan.title)}</h4>
            <div class="dashboard-plan-image-wrap">
                ${plan.image
                    ? `<img src="${plan.image}" alt="${escapeHtml(plan.title)}" />`
                    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#cbd5e1;"><i class="fa-solid fa-seedling" style="font-size:2.25rem;"></i></div>`
                }
            </div>
            <div class="dashboard-plan-tags">
                ${tagNames.map((p) => `<span class="dashboard-plan-tag">${escapeHtml(p)}</span>`).join('')}
            </div>
            <button type="button" onclick="openJournal('${plan.id}')" class="dashboard-btn-journal">
                <i class="fa-solid fa-book-open"></i> Open Garden Journal
            </button>
        </div>
    `;
    }).join('');

    const addCardHtml = isPreviewMode()
        ? ''
        : `
            <div class="dashboard-plan-add-placeholder">
                <a href="https://app.flourishingplants.com/" class="dashboard-plan-add-link" title="Go to Garden Planner to add a new plan" aria-label="Go to Garden Planner to add a new plan">
                    <i class="fa-solid fa-circle-plus"></i>
                </a>
                <span class="dashboard-plan-tag">Add New Garden Plan</span>
            </div>
        `;

    grid.innerHTML = cardsHtml + addCardHtml;
}

let currentJournalPlanYear = null;

function getPlanYear(planId) {
    const plan = Object.values(gardenData).flat().find(p => p.id === planId);
    return plan ? (plan.year || new Date().getFullYear()) : new Date().getFullYear();
}

/** Parse display date like "March 14" or "Sept 10" to YYYY-MM-DD for sorting. Uses planYear for the year. */
function parseDisplayDateToSortKey(dateStr, planYear) {
    if (!dateStr || !planYear) return '0000-00-00';
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const s = String(dateStr).trim().toLowerCase();
    const match = s.match(/(\d{1,2})\s*$/); // day at end
    const day = match ? parseInt(match[1], 10) : 1;
    for (let m = 0; m < 12; m++) {
        if (s.startsWith(months[m]) || s.includes(months[m])) {
            const month = String(m + 1).padStart(2, '0');
            const dayStr = String(day).padStart(2, '0');
            return `${planYear}-${month}-${dayStr}`;
        }
    }
    return '0000-00-00';
}

function getEntrySortKey(entry, planYear) {
    return entry.dateSort || parseDisplayDateToSortKey(entry.date, planYear) || '0000-00-00';
}

/**
 * Base crop name for a grid/journal label when that label is a variety (Firestore plantBaseByDisplay).
 */
function getJournalDisplayBaseForLabel(displayName, baseMap) {
    if (!baseMap || typeof baseMap !== 'object') return null;
    const d = String(displayName);
    if (Object.prototype.hasOwnProperty.call(baseMap, d)) {
        const v = baseMap[d];
        if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    const mk = Object.keys(baseMap).find((key) => key === d || key.trim() === d.trim());
    if (mk != null && baseMap[mk] != null && String(baseMap[mk]).trim() !== '') {
        return String(baseMap[mk]).trim();
    }
    return null;
}

/**
 * Rows for bed-plan journal varieties: names in summary order when plantCounts exists,
 * else unique order from plan.plants with counts aggregated (legacy).
 */
function getBedJournalVarietyRows(plan) {
    const rawPlants = plan.plants || [];
    const stored = plan.plantCounts && typeof plan.plantCounts === 'object' ? plan.plantCounts : null;
    const order = [];
    const seen = new Set();
    for (const raw of rawPlants) {
        const name = String(raw);
        if (!name.trim()) continue;
        if (seen.has(name)) continue;
        seen.add(name);
        order.push(name);
    }
    if (stored) {
        for (const k of Object.keys(stored)) {
            if (seen.has(k)) continue;
            seen.add(k);
            order.push(k);
        }
    }
    const countFor = (name) => {
        if (stored) {
            const v = stored[name];
            if (v != null && !Number.isNaN(Number(v))) return Math.max(0, Number(v));
            const mk = Object.keys(stored).find((key) => key === name || key.trim() === String(name).trim());
            if (mk != null && stored[mk] != null && !Number.isNaN(Number(stored[mk]))) {
                return Math.max(0, Number(stored[mk]));
            }
        }
        let c = 0;
        const target = String(name).trim();
        for (const raw of rawPlants) {
            if (String(raw).trim() === target) c++;
        }
        return c;
    };
    return order.map((name) => ({ name, count: countFor(name) }));
}

async function openJournal(planId, fromVarietyLink = false) {
    const plan = Object.values(gardenData).flat().find(p => p.id === planId);
    if (!plan) return;
    if (fromVarietyLink) {
        // Coming from a bed link on an overview: use the overview we were just viewing (currentJournalPlanId), not the first overview in the list
        const currentPlan = currentJournalPlanId ? Object.values(gardenData).flat().find(p => p.id === currentJournalPlanId) : null;
        const currentIsOverview = currentPlan && currentPlan.title === 'Garden Plan Overview';
        openedFromGardenPlanOverviewPlanId = currentIsOverview ? currentJournalPlanId : null;
    } else {
        openedFromGardenPlanOverviewPlanId = null;
    }
    // Analytics: treat Journal as a "page visit" (SPA pageview)
    trackPageView(`/journal.html?plan=${encodeURIComponent(planId)}`, `Journal — ${plan.title || 'Garden Plan'}`);
    currentJournalPlanId = planId;
    currentJournalPlanYear = plan.year ?? new Date().getFullYear();
    // Onboarding: mark "sample journal entries viewed" when a user opens the sample plan.
    // This works for the non-premium preview dashboard too (sample plan is still available there).
    try {
        if (planId === PREVIEW_SAMPLE_PLAN_ID) {
            localStorage.setItem('np_onboarding_journal_sample_opened_v1', '1');
        }
    } catch (_) {
        // Ignore storage quota/privacy mode issues.
    }
    await loadJournalEntries(planId);
    const listEl = document.getElementById('archive-list');
    if (listEl) listEl.classList.add('hidden');
    const detail = document.getElementById('journal-detail');
    if (detail) detail.classList.remove('hidden');
    const titleEl = document.getElementById('journal-title');
    if (titleEl) titleEl.textContent = plan.title;
    const subEl = document.getElementById('journal-subtitle');
    if (subEl) subEl.textContent = `${plan.month} ${plan.year} Season Records`;
    const pictureEl = document.getElementById('journal-garden-picture');
    if (pictureEl) {
        if (plan.image) {
            pictureEl.innerHTML = `<img src="${plan.image}" alt="${escapeHtml(plan.title)}" />`;
            pictureEl.setAttribute('role', 'button');
            pictureEl.setAttribute('tabindex', '0');
            pictureEl.setAttribute('aria-label', 'View garden image full size');
        } else {
            pictureEl.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#cbd5e1;"><i class="fa-solid fa-seedling" style="font-size:2.25rem;"></i></div>`;
            pictureEl.removeAttribute('role');
            pictureEl.removeAttribute('tabindex');
            pictureEl.removeAttribute('aria-label');
        }
    }
    const plantInfoEl = document.getElementById('journal-plant-info-picture');
    if (plantInfoEl) {
        if (plan.plantInfoImage) {
            plantInfoEl.innerHTML = `<img src="${plan.plantInfoImage}" alt="Garden Plant Information" />`;
            plantInfoEl.setAttribute('role', 'button');
            plantInfoEl.setAttribute('tabindex', '0');
            plantInfoEl.setAttribute('aria-label', 'View Garden Plant Information full size');
            plantInfoEl.classList.remove('hidden');
        } else {
            plantInfoEl.innerHTML = '';
            plantInfoEl.removeAttribute('role');
            plantInfoEl.removeAttribute('tabindex');
            plantInfoEl.removeAttribute('aria-label');
            plantInfoEl.classList.add('hidden');
        }
    }
    const calendarPictureEl = document.getElementById('journal-calendar-picture');
    if (calendarPictureEl) {
        if (plan.calendarImage) {
            calendarPictureEl.innerHTML = `<img src="${plan.calendarImage}" alt="Garden Calendar" />`;
            calendarPictureEl.setAttribute('role', 'button');
            calendarPictureEl.setAttribute('tabindex', '0');
            calendarPictureEl.setAttribute('aria-label', 'View Garden Calendar full size');
            calendarPictureEl.classList.remove('hidden');
        } else {
            calendarPictureEl.innerHTML = '';
            calendarPictureEl.removeAttribute('role');
            calendarPictureEl.removeAttribute('tabindex');
            calendarPictureEl.removeAttribute('aria-label');
            calendarPictureEl.classList.add('hidden');
        }
    }
    renderJournalEntries(planId);
    const plantList = document.getElementById('journal-plants');
    if (plantList) {
        const isGardenPlanOverview = plan.title === 'Garden Plan Overview';
        const allPlans = Object.values(gardenData).flat();
        if (isGardenPlanOverview) {
            const items = (plan.plants || []).map(p => {
                const matchedPlan = allPlans.find(pl => pl.title && String(pl.title).trim() === String(p).trim());
                const hasPlan = !!matchedPlan;
                const content = hasPlan
                    ? `<a href="#" class="dashboard-journal-variety-link" data-plan-id="${escapeHtml(matchedPlan.id)}">${escapeHtml(p)}</a>`
                    : `<span class="dashboard-journal-variety-removed">${escapeHtml(p)} <span class="dashboard-journal-variety-removed-note">bed removed</span></span>`;
                const html = `
            <li class="dashboard-flex-between">
                <span>${content}</span>
                <i class="fa-solid fa-circle-check" style="color:#10b981;"></i>
            </li>
        `;
                return { hasPlan, html };
            });
            // Show available beds (with plans) first, then removed beds
            items.sort((a, b) => {
                const aVal = a.hasPlan ? 0 : 1;
                const bVal = b.hasPlan ? 0 : 1;
                return aVal - bVal;
            });
            plantList.innerHTML = items.map(item => item.html).join('');
        } else {
            const baseMap =
                plan.plantBaseByDisplay && typeof plan.plantBaseByDisplay === 'object'
                    ? plan.plantBaseByDisplay
                    : null;
            const rows = getBedJournalVarietyRows(plan);
            plantList.innerHTML = rows.map(({ name, count }) => {
                const n = count;
                const base = getJournalDisplayBaseForLabel(name, baseMap);
                const trimmedName = String(name).trim();
                const isVarietyRow = base != null && base !== trimmedName;
                const leftInner = isVarietyRow
                    ? `<span class="dashboard-journal-plant-count">${n}</span><span>${escapeHtml(base)} - ${escapeHtml(trimmedName)}</span>`
                    : `<span class="dashboard-journal-plant-count">${n}</span><span>${escapeHtml(trimmedName)}</span>`;
                return `
            <li class="dashboard-flex-between dashboard-journal-variety-item">
                <span class="dashboard-journal-variety-left">
                    ${leftInner}
                </span>
                <i class="fa-solid fa-circle-check" style="color:#10b981;"></i>
            </li>
        `;
            }).join('');
        }
    }
    const gotoOverviewWrap = document.getElementById('journal-goto-overview-wrap');
    const gotoOverviewBtn = document.getElementById('journal-goto-overview-btn');
    if (gotoOverviewWrap && gotoOverviewBtn) {
        const showGotoOverview = openedFromGardenPlanOverviewPlanId && openedFromGardenPlanOverviewPlanId !== planId;
        gotoOverviewWrap.classList.toggle('hidden', !showGotoOverview);
        if (showGotoOverview) {
            gotoOverviewBtn.onclick = () => openJournal(openedFromGardenPlanOverviewPlanId);
        }
    }
}

/** Calendar event type for display: planting (sow/transplant), harvest, or other */
function getCalendarEventClass(type) {
    if (type === 'Planting') return 'event-planting';
    if (type === 'Harvest') return 'event-harvest';
    return 'event-other';
}

/** Use same icon as journal: entry's icon or entryTypeConfig for this type. */
function getCalendarEventIcon(entry) {
    const cfg = entryTypeConfig[entry.type] || entryTypeConfig.Note;
    return entry.icon || cfg.icon;
}

/** Icon color for calendar (matches journal entry type). */
function getCalendarEventIconColor(entry) {
    const cfg = entryTypeConfig[entry.type] || entryTypeConfig.Note;
    return cfg.iconColor || '#64748b';
}

function getAllPlanIds() {
    return Object.values(gardenData).flat().map(p => p.id);
}

async function loadAllJournalEntriesForCalendar() {
    const planIds = getAllPlanIds();
    await Promise.all(planIds.map((planId) => loadJournalEntries(planId)));
}

function getPlanTitle(planId) {
    const plan = Object.values(gardenData).flat().find(p => p.id === planId);
    return plan ? plan.title : 'Garden';
}

function getSelectedGlobalCalendarMonth() {
    const monthInput = document.getElementById('global-calendar-month');
    return monthInput ? parseInt(monthInput.value, 10) : new Date().getMonth() + 1;
}

function setActiveGlobalCalendarMonth(month, options = {}) {
    const monthInput = document.getElementById('global-calendar-month');
    const monthButtons = document.querySelectorAll('.journal-calendar-month-btn');
    const monthValue = Number.isFinite(month) ? month : new Date().getMonth() + 1;
    if (monthInput) monthInput.value = String(monthValue);
    monthButtons.forEach((btn) => {
        const isActive = parseInt(btn.dataset.month || '', 10) === monthValue;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    if (options.scrollIntoView) {
        const activeBtn = document.querySelector(`.journal-calendar-month-btn[data-month="${monthValue}"]`);
        activeBtn?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
}

function populateGlobalCalendarYearDropdown() {
    const sel = document.getElementById('global-calendar-year');
    if (!sel) return;
    const years = new Set(Object.keys(gardenData).map(Number));
    const current = new Date().getFullYear();
    const selectedYear = parseInt(sel.value, 10);
    const selectedMonth = getSelectedGlobalCalendarMonth();
    for (let y = current - 2; y <= current + 2; y++) years.add(y);
    const sorted = [...years].sort((a, b) => a - b);
    sel.innerHTML = sorted.map(y => `<option value="${y}">${y}</option>`).join('');
    setActiveGlobalCalendarMonth(Number.isFinite(selectedMonth) ? selectedMonth : new Date().getMonth() + 1);
    sel.value = years.has(selectedYear) ? String(selectedYear) : String(current);
}

function renderSelectedGlobalCalendar(options = {}) {
    const { useCurrentDate = false } = options;
    populateGlobalCalendarYearDropdown();
    const yearSelect = document.getElementById('global-calendar-year');
    if (useCurrentDate) {
        const now = new Date();
        setActiveGlobalCalendarMonth(now.getMonth() + 1);
        if (yearSelect) yearSelect.value = String(now.getFullYear());
    }
    const month = getSelectedGlobalCalendarMonth();
    const year = yearSelect ? parseInt(yearSelect.value, 10) : new Date().getFullYear();
    renderGlobalCalendar(month, year);
}

function hasCalendarJournalCache() {
    return getAllPlanIds().some((planId) => Array.isArray(journals[planId]));
}

function preloadCalendarEntries(options = {}) {
    const { force = false } = options;
    if (calendarPreloadPromise) return calendarPreloadPromise;
    const planIds = getAllPlanIds();
    calendarPreloadPromise = Promise.all(planIds.map((planId) => loadJournalEntries(planId, { force })))
        .finally(() => {
            calendarPreloadPromise = null;
        });
    return calendarPreloadPromise;
}

function refreshCalendarIfVisible() {
    const calendarView = document.getElementById('view-calendar');
    if (calendarView && !calendarView.classList.contains('hidden')) {
        renderSelectedGlobalCalendar();
    }
}

/** First whitespace-delimited word of plan/garden name (mobile shows this only). */
function firstWordOfGardenName(name) {
    if (!name || typeof name !== 'string') return '';
    const trimmed = name.trim();
    if (!trimmed) return '';
    return trimmed.split(/\s+/)[0] || '';
}

/** Markup: full title + first word; CSS shows one or the other by viewport. */
function gardenNameMarkup(planTitle) {
    const raw = planTitle || '';
    const full = escapeHtml(raw);
    const short = escapeHtml(firstWordOfGardenName(raw));
    return `<span class="journal-calendar-garden-name"><span class="journal-calendar-garden-name-full">${full}</span><span class="journal-calendar-garden-name-short">${short}</span></span>`;
}

async function onShowCalendarTab() {
    const hadCachedEntries = hasCalendarJournalCache();
    if (hadCachedEntries) renderSelectedGlobalCalendar({ useCurrentDate: true });
    const preloadPromise = preloadCalendarEntries();
    if (!hadCachedEntries) {
        await preloadPromise;
        renderSelectedGlobalCalendar({ useCurrentDate: true });
        return;
    }
    preloadPromise.then(() => {
        refreshCalendarIfVisible();
    }).catch(() => {});
}

function renderGlobalCalendar(month, year) {
    const tbody = document.getElementById('global-calendar-body');
    if (!tbody) return;

    const entriesByDate = {};
    const planIds = getAllPlanIds();
    planIds.forEach((planId) => {
        const planYear = getPlanYear(planId);
        const entries = journals[planId] || [];
        const planTitle = getPlanTitle(planId);
        entries.forEach((e) => {
            const sortKey = e.dateSort || parseDisplayDateToSortKey(e.date, planYear);
            if (!/^\d{4}-\d{2}-\d{2}$/.test(sortKey)) return;
            const [y, m, d] = sortKey.split('-').map(Number);
            if (y !== year || m !== month) return;
            if (!entriesByDate[d]) entriesByDate[d] = [];
            entriesByDate[d].push({ ...e, planTitle });
        });
    });

    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0).getDate();
    const startWeekday = (firstDay.getDay() + 6) % 7;

    let day = 1;
    const rows = [];
    let row = [];
    for (let i = 0; i < startWeekday; i++) row.push('<td></td>');
    while (day <= lastDay) {
        const events = entriesByDate[day] || [];
        const eventHtml = events.map((e) => {
            const cls = getCalendarEventClass(e.type);
            const icon = getCalendarEventIcon(e);
            const iconColor = getCalendarEventIconColor(e);
            let label = e.type;
            if (e.type === 'Planting') {
                const entryLower = (e.entry || '').toLowerCase();
                label = entryLower.includes('transplant') ? 'Transplant' : 'Planting';
            }
            const gardenStamp = gardenNameMarkup(e.planTitle);
            return `<div class="journal-calendar-event ${cls}"><i class="fa-solid ${icon}" aria-hidden="true" style="color:${escapeHtml(iconColor)}"></i><span class="journal-calendar-event-text">${escapeHtml(label)}${gardenStamp}</span></div>`;
        }).join('');
        row.push(`<td data-day="${day}" data-month="${month}" data-year="${year}"><div class="journal-calendar-cell-inner"><div class="journal-calendar-day-num">${day}</div><div class="journal-calendar-cell-events">${eventHtml}</div></div></td>`);
        day++;
        if (row.length === 7) {
            rows.push('<tr>' + row.join('') + '</tr>');
            row = [];
        }
    }
    if (row.length) {
        while (row.length < 7) row.push('<td></td>');
        rows.push('<tr>' + row.join('') + '</tr>');
    }
    tbody.innerHTML = rows.length ? rows.join('') : '<tr><td colspan="7" style="text-align:center;padding:1rem;color:#64748b;">No entries this month.</td></tr>';
}

/** Return entries for a given date (from all plans) for the calendar overlay. */
function getEntriesForDate(year, month, day) {
    const entriesByDate = {};
    const planIds = getAllPlanIds();
    planIds.forEach((planId) => {
        const planYear = getPlanYear(planId);
        const entries = journals[planId] || [];
        const planTitle = getPlanTitle(planId);
        entries.forEach((e) => {
            const sortKey = e.dateSort || parseDisplayDateToSortKey(e.date, planYear);
            if (!/^\d{4}-\d{2}-\d{2}$/.test(sortKey)) return;
            const [y, m, d] = sortKey.split('-').map(Number);
            if (y !== year || m !== month || d !== day) return;
            if (!entriesByDate[d]) entriesByDate[d] = [];
            entriesByDate[d].push({ ...e, planTitle });
        });
    });
    return entriesByDate[day] || [];
}

function openJournalDateEntriesOverlay(day, month, year) {
    const overlay = document.getElementById('journal-date-entries-overlay');
    const titleEl = document.getElementById('journal-date-entries-overlay-title');
    const contentEl = document.getElementById('journal-date-entries-overlay-content');
    if (!overlay || !titleEl || !contentEl) return;
    const entries = getEntriesForDate(year, month, day);
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dateLabel = `${monthNames[month - 1]} ${day}, ${year}`;
    titleEl.textContent = dateLabel;
    const eventHtml = entries.map((e) => {
        const cls = getCalendarEventClass(e.type);
        const icon = getCalendarEventIcon(e);
        const iconColor = getCalendarEventIconColor(e);
        let label = e.type;
        if (e.type === 'Planting') {
            const entryLower = (e.entry || '').toLowerCase();
            label = entryLower.includes('transplant') ? 'Transplant' : 'Planting';
        }
        const text = escapeHtml(e.entry || '');
        const gardenStamp = gardenNameMarkup(e.planTitle);
        const photoHtml = e.photoUrl
            ? `<button type="button" class="journal-date-entry-photo" aria-label="View entry photo full size" title="Click to view full size" onclick="openJournalPictureOverlay(this.querySelector('img').src, 'Entry photo')"><img src="${escapeHtml(e.photoUrl)}" alt="Entry photo" loading="lazy" /></button>`
            : '';
        return `<div class="journal-calendar-event ${cls}"><div class="journal-date-entry-main"><i class="fa-solid ${icon}" aria-hidden="true" style="color:${escapeHtml(iconColor)}"></i><span class="journal-calendar-event-text">${escapeHtml(label)}: ${text}${gardenStamp}</span></div>${photoHtml}</div>`;
    }).join('');
    contentEl.innerHTML = eventHtml || '<p style="color:#64748b;margin:0;">No entries for this date.</p>';
    overlay.setAttribute('aria-hidden', 'false');
    overlay.classList.add('show');
    document.addEventListener('keydown', onJournalDateEntriesOverlayKeydown);
}

function closeJournalDateEntriesOverlay() {
    const overlay = document.getElementById('journal-date-entries-overlay');
    if (overlay) {
        overlay.classList.remove('show');
        overlay.setAttribute('aria-hidden', 'true');
    }
    document.removeEventListener('keydown', onJournalDateEntriesOverlayKeydown);
}

function onJournalDateEntriesOverlayKeydown(e) {
    if (e.key === 'Escape') closeJournalDateEntriesOverlay();
}

function initGlobalCalendarHandlers() {
    const monthButtons = document.querySelectorAll('.journal-calendar-month-btn');
    const yearSelect = document.getElementById('global-calendar-year');
    const render = () => {
        const month = getSelectedGlobalCalendarMonth();
        const year = yearSelect ? parseInt(yearSelect.value, 10) : new Date().getFullYear();
        renderGlobalCalendar(month, year);
    };
    if (yearSelect) yearSelect.addEventListener('change', render);
    monthButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
            const month = parseInt(btn.dataset.month || '', 10);
            if (!month) return;
            setActiveGlobalCalendarMonth(month, { scrollIntoView: true });
            render();
        });
    });

    const table = document.getElementById('global-calendar-table');
    if (table) {
        table.addEventListener('click', (e) => {
            const td = e.target.closest('td[data-day]');
            if (!td) return;
            const day = parseInt(td.getAttribute('data-day'), 10);
            const month = parseInt(td.getAttribute('data-month'), 10);
            const year = parseInt(td.getAttribute('data-year'), 10);
            if (!td.querySelector('.journal-calendar-event')) return;
            openJournalDateEntriesOverlay(day, month, year);
        });
    }
}

function renderJournalEntries(planId) {
    const entriesDiv = document.getElementById('journal-entries');
    if (!entriesDiv) return;
    const entries = journals[planId] || [];
    const cfgDefault = entryTypeConfig.Note || {};
    entriesDiv.innerHTML = entries.length ? entries.map((e, idx) => {
        const cfg = entryTypeConfig[e.type] || cfgDefault;
        const icon = e.icon || cfg.icon;
        const color = e.color || cfg.color;
        const bgLight = cfg.bgLight || 'dashboard-entry-bg-slate';
        const planIdEsc = escapeHtml(planId);
        const idxAttr = idx;
        const actionsHtml = isPreviewMode()
            ? ''
            : `
                        <div class="dashboard-journal-entry-actions">
                            <button type="button" onclick="openEditEntryModal('${planIdEsc}', ${idxAttr})" class="edit" title="Edit entry"><i class="fa-solid fa-pen" style="font-size:0.75rem;"></i></button>
                            <button type="button" onclick="deleteJournalEntry('${planIdEsc}', ${idxAttr})" class="delete" title="Delete entry"><i class="fa-solid fa-trash-can" style="font-size:0.75rem;"></i></button>
                        </div>
                    `;
        return `
        <div class="dashboard-journal-entry-row">
            <div class="dashboard-journal-entry-icon ${color}">
                <i class="fa-solid ${icon}"></i>
            </div>
            <div class="dashboard-journal-entry-body ${bgLight}">
                <div class="dashboard-journal-entry-content">
                    <div class="dashboard-journal-entry-meta">
                        <div class="dashboard-flex-center dashboard-gap-2" style="flex-wrap:wrap;">
                            <span class="dashboard-journal-entry-date">${escapeHtml(e.date)}</span>
                            <span class="dashboard-journal-entry-type">${escapeHtml(e.type)}</span>
                        </div>
                        ${actionsHtml}
                    </div>
                    <p class="dashboard-journal-entry-text">${escapeHtml(e.entry)}</p>
                </div>
                ${e.photoUrl ? `<div class="dashboard-journal-entry-photo visible"><img src="${escapeHtml(e.photoUrl)}" alt="Entry photo" loading="lazy" role="button" tabindex="0" title="Click to view full size" aria-label="View entry photo full size" onclick="openJournalPictureOverlay(this.src, 'Entry photo')" onkeydown="if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openJournalPictureOverlay(this.src, 'Entry photo'); }" /></div>` : '<div class="dashboard-journal-entry-photo"></div>'}
            </div>
        </div>
        `;
    }).join('') : '<div style="text-align:center;padding:2.5rem;color:#94a3b8;font-style:italic;">No journal entries yet for this plan.</div>';
}

let editingEntryIndex = null;
/** Pending photo for current modal: { blob } to upload, { remove: true } to clear existing, or null. */
let pendingEntryPhoto = null;
let isSubmittingNewEntry = false;

const ENTRY_PHOTO_MAX_BYTES = 200 * 1024;
const JOURNAL_MAX_PHOTOS = 5;

function getJournalPhotoCount(planId) {
    const entries = journals[planId] || [];
    return entries.filter((e) => e.photoUrl).length;
}

function setNewEntrySubmitState(isSubmitting, isEditing = editingEntryIndex !== null) {
    isSubmittingNewEntry = isSubmitting;
    const submitBtn = document.getElementById('new-entry-modal-submit-btn');
    if (!submitBtn) return;

    submitBtn.disabled = isSubmitting;
    if (isSubmitting) {
        submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Uploading...';
        submitBtn.setAttribute('aria-busy', 'true');
    } else {
        submitBtn.innerHTML = isEditing
            ? '<i class="fa-solid fa-check"></i> Save changes'
            : '<i class="fa-solid fa-check"></i> Add Entry';
        submitBtn.removeAttribute('aria-busy');
    }
}

/**
 * Compress an image file to under maxBytes (WebP). Returns a Blob.
 * @param {File} file
 * @param {number} maxBytes
 * @returns {Promise<Blob>}
 */
function compressImageToMaxSize(file, maxBytes) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            const maxDim = 1600;
            const minDim = 700;
            let width = img.width;
            let height = img.height;
            if (width > maxDim || height > maxDim) {
                if (width >= height) {
                    height = Math.round((height * maxDim) / width);
                    width = maxDim;
                } else {
                    width = Math.round((width * maxDim) / height);
                    height = maxDim;
                }
            }
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Canvas not supported'));
                return;
            }

            const renderBlob = (currentWidth, currentHeight, quality) => new Promise((resolveBlob, rejectBlob) => {
                canvas.width = currentWidth;
                canvas.height = currentHeight;
                ctx.clearRect(0, 0, currentWidth, currentHeight);
                ctx.drawImage(img, 0, 0, currentWidth, currentHeight);
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            rejectBlob(new Error('Failed to compress image'));
                            return;
                        }
                        resolveBlob(blob);
                    },
                    'image/webp',
                    quality
                );
            });

            (async () => {
                const qualitySteps = [0.82, 0.72, 0.62, 0.52, 0.42, 0.34];
                let currentWidth = width;
                let currentHeight = height;
                let bestBlob = null;

                for (let scaleAttempt = 0; scaleAttempt < 5; scaleAttempt += 1) {
                    for (const quality of qualitySteps) {
                        const blob = await renderBlob(currentWidth, currentHeight, quality);
                        bestBlob = blob;
                        if (blob.size <= maxBytes) {
                            resolve(blob);
                            return;
                        }
                    }

                    const longestSide = Math.max(currentWidth, currentHeight);
                    if (longestSide <= minDim) {
                        break;
                    }

                    const scaledLongestSide = Math.round(longestSide * 0.85);
                    const nextScale = Math.max(minDim, scaledLongestSide) / longestSide;
                    const nextWidth = Math.max(1, Math.round(currentWidth * nextScale));
                    const nextHeight = Math.max(1, Math.round(currentHeight * nextScale));
                    if (nextWidth === currentWidth && nextHeight === currentHeight) break;
                    currentWidth = nextWidth;
                    currentHeight = nextHeight;
                }

                resolve(bestBlob);
            })().catch(reject);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image'));
        };
        img.src = url;
    });
}

function clearEntryPhotoInModal() {
    const preview = document.getElementById('entry-photo-preview');
    const previewImg = document.getElementById('entry-photo-preview-img');
    const input = document.getElementById('entry-photo-input');
    if (preview) preview.classList.add('hidden');
    if (previewImg) previewImg.removeAttribute('src');
    if (input) input.value = '';
    if (pendingEntryPhoto?.objectUrl) URL.revokeObjectURL(pendingEntryPhoto.objectUrl);
    pendingEntryPhoto = null;
}

function showEntryPhotoPreview(src) {
    const preview = document.getElementById('entry-photo-preview');
    const previewImg = document.getElementById('entry-photo-preview-img');
    if (previewImg) previewImg.src = src;
    if (preview) preview.classList.remove('hidden');
}

function openNewEntryModal() {
    if (isPreviewMode()) return;
    if (!currentJournalPlanId) return;
    editingEntryIndex = null;
    const form = document.getElementById('new-entry-form');
    if (form) form.reset();
    clearEntryPhotoInModal();
    const picker = document.getElementById('entry-date-picker');
    if (picker) picker.value = '';
    const titleEl = document.getElementById('new-entry-modal-title');
    if (titleEl) titleEl.textContent = 'New Journal Entry';
    setNewEntrySubmitState(false, false);
    const modal = document.getElementById('new-entry-modal');
    if (modal) modal.classList.add('show');
    document.addEventListener('keydown', onNewEntryModalKeydown);
}

function openEditEntryModal(planId, index) {
    if (isPreviewMode()) return;
    const entries = journals[planId] || [];
    const e = entries[index];
    if (!e) return;
    editingEntryIndex = index;
    const dateEl = document.getElementById('entry-date');
    if (dateEl) dateEl.value = e.date;
    const picker = document.getElementById('entry-date-picker');
    if (picker) picker.value = '';
    const typeEl = document.getElementById('entry-type');
    if (typeEl) typeEl.value = e.type;
    const textEl = document.getElementById('entry-text');
    if (textEl) textEl.value = e.entry;
    clearEntryPhotoInModal();
    if (e.photoUrl) showEntryPhotoPreview(e.photoUrl);
    const titleEl = document.getElementById('new-entry-modal-title');
    if (titleEl) titleEl.textContent = 'Edit Journal Entry';
    setNewEntrySubmitState(false, true);
    const modal = document.getElementById('new-entry-modal');
    if (modal) modal.classList.add('show');
    document.addEventListener('keydown', onNewEntryModalKeydown);
}

function formatPickedDate(isoDate) {
    if (!isoDate) return '';
    const parts = isoDate.split('-').map(Number);
    const y = parts[0], m = parts[1], d = parts[2];
    const months = ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
    return months[m - 1] + ' ' + d;
}

function initDatePickerSync() {
    const picker = document.getElementById('entry-date-picker');
    const text = document.getElementById('entry-date');
    if (!picker || !text) return;
    picker.addEventListener('change', function () {
        text.value = formatPickedDate(this.value);
    });
}

function initEntryPhotoHandlers() {
    const trigger = document.getElementById('entry-photo-trigger');
    const input = document.getElementById('entry-photo-input');
    const removeBtn = document.getElementById('entry-photo-remove');
    if (trigger && input) {
        trigger.addEventListener('click', () => input.click());
    }
    if (input) {
        input.addEventListener('change', async () => {
            const file = input.files && input.files[0];
            if (!file || !file.type.startsWith('image/')) return;
            const planId = currentJournalPlanId;
            if (planId) {
                const currentCount = getJournalPhotoCount(planId);
                const existing = editingEntryIndex !== null && journals[planId] ? journals[planId][editingEntryIndex] : null;
                const isReplacing = existing?.photoUrl;
                if (!isReplacing && currentCount >= JOURNAL_MAX_PHOTOS) {
                    input.value = '';
                    alert(`This journal already has the maximum of ${JOURNAL_MAX_PHOTOS} photos. Remove a photo from another entry to add a new one.`);
                    return;
                }
            }
            try {
                const blob = await compressImageToMaxSize(file, ENTRY_PHOTO_MAX_BYTES);
                if (pendingEntryPhoto?.objectUrl) URL.revokeObjectURL(pendingEntryPhoto.objectUrl);
                const objectUrl = URL.createObjectURL(blob);
                pendingEntryPhoto = { blob, objectUrl };
                showEntryPhotoPreview(objectUrl);
            } catch (err) {
                console.error('Photo compress failed:', err);
            }
        });
    }
    if (removeBtn) {
        removeBtn.addEventListener('click', () => {
            const preview = document.getElementById('entry-photo-preview');
            const previewImg = document.getElementById('entry-photo-preview-img');
            if (preview) preview.classList.add('hidden');
            if (previewImg) previewImg.removeAttribute('src');
            input.value = '';
            if (pendingEntryPhoto?.objectUrl) URL.revokeObjectURL(pendingEntryPhoto.objectUrl);
            if (editingEntryIndex !== null && currentJournalPlanId && journals[currentJournalPlanId]) {
                const existing = journals[currentJournalPlanId][editingEntryIndex];
                pendingEntryPhoto = existing?.photoUrl ? { remove: true } : null;
            } else {
                pendingEntryPhoto = null;
            }
        });
    }
}

function closeNewEntryModal() {
    editingEntryIndex = null;
    clearEntryPhotoInModal();
    const modal = document.getElementById('new-entry-modal');
    if (modal) modal.classList.remove('show');
    document.removeEventListener('keydown', onNewEntryModalKeydown);
}

function onNewEntryModalKeydown(e) {
    if (e.key === 'Escape') closeNewEntryModal();
}

function openJournalPictureOverlay(src, alt) {
    const overlay = document.getElementById('journal-picture-overlay');
    const img = document.getElementById('journal-picture-overlay-img');
    if (!overlay || !img) return;
    img.src = src;
    img.alt = alt || 'Garden image';
    overlay.setAttribute('aria-hidden', 'false');
    overlay.classList.add('show');
    document.addEventListener('keydown', onJournalPictureOverlayKeydown);
}

function closeJournalPictureOverlay() {
    const overlay = document.getElementById('journal-picture-overlay');
    const img = document.getElementById('journal-picture-overlay-img');
    if (overlay) {
        overlay.classList.remove('show');
        overlay.setAttribute('aria-hidden', 'true');
    }
    if (img) img.removeAttribute('src');
    document.removeEventListener('keydown', onJournalPictureOverlayKeydown);
}

function onJournalPictureOverlayKeydown(e) {
    if (e.key === 'Escape') closeJournalPictureOverlay();
}

async function submitNewEntry(ev) {
    ev.preventDefault();
    if (isPreviewMode()) return;
    if (isSubmittingNewEntry) return;
    if (!currentJournalPlanId) return;
    const dateEl = document.getElementById('entry-date');
    const typeEl = document.getElementById('entry-type');
    const textEl = document.getElementById('entry-text');
    if (!dateEl || !typeEl || !textEl) return;
    const date = dateEl.value.trim();
    const type = typeEl.value;
    const entry = textEl.value.trim();
    if (!date || !entry) return;
    const picker = document.getElementById('entry-date-picker');
    const planYear = currentJournalPlanYear ?? new Date().getFullYear();
    const dateSort = (picker && picker.value) ? picker.value : parseDisplayDateToSortKey(date, planYear);
    const uid = auth?.currentUser?.uid;
    if (!uid || !db) { renderJournalEntries(currentJournalPlanId); closeNewEntryModal(); return; }
    if (!journals[currentJournalPlanId]) journals[currentJournalPlanId] = [];
    const planId = currentJournalPlanId;
    if (pendingEntryPhoto?.blob) {
        const currentCount = getJournalPhotoCount(planId);
        const existing = editingEntryIndex !== null ? journals[planId][editingEntryIndex] : null;
        const isReplacing = !!existing?.photoUrl;
        if (!isReplacing && currentCount >= JOURNAL_MAX_PHOTOS) {
            alert(`This journal already has the maximum of ${JOURNAL_MAX_PHOTOS} photos. Remove a photo from another entry to add a new one.`);
            return;
        }
    }
    const cfg = entryTypeConfig[type] || entryTypeConfig.Note;
    const payload = { date, dateSort, entry, type, icon: cfg.icon, color: cfg.color };
    const entryRef = (id) => doc(db, 'users', uid, 'gardenPlans', planId, 'journalEntries', id);
    const isEditing = editingEntryIndex !== null;
    setNewEntrySubmitState(true, isEditing);

    try {
        if (isEditing) {
            const existing = journals[planId][editingEntryIndex];
            if (!existing?.id) {
                closeNewEntryModal();
                return;
            }
            let photoUrl = existing.photoUrl ?? null;
            if (pendingEntryPhoto?.remove && existing.photoUrl && storage) {
                try {
                    const path = getJournalEntryPhotoPath(uid, planId, existing.id);
                    await deleteObject(ref(storage, path));
                } catch (_) { /* ignore */ }
                photoUrl = null;
            } else if (pendingEntryPhoto?.blob && storage) {
                const path = getJournalEntryPhotoPath(uid, planId, existing.id);
                const storageRef = ref(storage, path);
                await uploadBytes(storageRef, pendingEntryPhoto.blob, { contentType: 'image/webp' });
                photoUrl = await getDownloadURL(storageRef);
            }
            payload.photoUrl = photoUrl;
            await updateDoc(entryRef(existing.id), payload);
            journals[planId][editingEntryIndex] = { ...payload, id: existing.id };
        } else {
            const col = collection(db, 'users', uid, 'gardenPlans', planId, 'journalEntries');
            const docRef = await addDoc(col, payload);
            let photoUrl = null;
            if (pendingEntryPhoto?.blob && storage) {
                const path = getJournalEntryPhotoPath(uid, planId, docRef.id);
                const storageRef = ref(storage, path);
                await uploadBytes(storageRef, pendingEntryPhoto.blob, { contentType: 'image/webp' });
                photoUrl = await getDownloadURL(storageRef);
                await updateDoc(entryRef(docRef.id), { photoUrl });
            }
            journals[planId].push({ ...payload, photoUrl, id: docRef.id });
        }

        journals[planId].sort((a, b) => getEntrySortKey(b, planYear).localeCompare(getEntrySortKey(a, planYear)));
        renderJournalEntries(planId);
        persistJournalCache();
        refreshCalendarIfVisible();
        closeNewEntryModal();
    } finally {
        setNewEntrySubmitState(false, editingEntryIndex !== null);
    }
}

async function deleteJournalEntry(planId, index) {
    if (isPreviewMode()) return;
    if (!confirm('Delete this journal entry?')) return;
    const entries = journals[planId] || [];
    const entry = entries[index];
    const uid = auth?.currentUser?.uid;
    if (uid && entry?.id) {
        if (entry.photoUrl && storage) {
            try {
                const path = getJournalEntryPhotoPath(uid, planId, entry.id);
                await deleteObject(ref(storage, path));
            } catch (_) { /* ignore */ }
        }
        if (db) await deleteDoc(doc(db, 'users', uid, 'gardenPlans', planId, 'journalEntries', entry.id));
    }
    entries.splice(index, 1);
    renderJournalEntries(planId);
    persistJournalCache();
    refreshCalendarIfVisible();
}

async function removePlan(planId) {
    if (isPreviewMode()) return;
    const id = planId || pendingRemovePlanId;
    if (!id) return;
    pendingRemovePlanId = null;
    closeRemovePlanModal();
    const uid = auth?.currentUser?.uid;
    if (uid && db) {
        const entriesCol = collection(db, 'users', uid, 'gardenPlans', id, 'journalEntries');
        const snap = await getDocs(entriesCol);
        for (const d of snap.docs) {
            const entryData = d.data();
            if (entryData?.photoUrl && storage) {
                try {
                    const photoRef = ref(storage, getJournalEntryPhotoPath(uid, id, d.id));
                    await deleteObject(photoRef);
                } catch { /* journal entry photo may not exist */ }
            }
            await deleteDoc(d.ref);
        }
        await deleteDoc(doc(db, 'users', uid, 'gardenPlans', id));
        if (storage) {
            try {
                const snapshotRef = ref(storage, getPlanSnapshotStoragePath(uid, id));
                await deleteObject(snapshotRef);
            } catch { /* snapshot may not exist */ }
            try {
                const plantInfoRef = ref(storage, getPlanPlantInfoStoragePath(uid, id));
                await deleteObject(plantInfoRef);
            } catch { /* plant info snapshot may not exist */ }
            try {
                const calendarRef = ref(storage, getPlanCalendarStoragePath(uid, id));
                await deleteObject(calendarRef);
            } catch { /* calendar snapshot may not exist */ }
        }
    }
    const yearKeys = Object.keys(gardenData);
    for (const y of yearKeys) {
        const arr = gardenData[y];
        const idx = arr.findIndex(p => p.id === id);
        if (idx !== -1) {
            arr.splice(idx, 1);
            break;
        }
    }
    delete journals[id];
    loadedJournalPlanIds.delete(id);
    journalLoadPromises.delete(id);
    persistJournalCache();
    if (currentJournalPlanId === id) closeJournal();
    renderPlans();
    refreshCalendarIfVisible();
}

function closeJournal() {
    const listEl = document.getElementById('archive-list');
    if (listEl) listEl.classList.remove('hidden');
    const detail = document.getElementById('journal-detail');
    if (detail) detail.classList.add('hidden');
}

// --- Seed Inventory (Firestore: users/{uid}/seedVault) ---
const SEED_STORAGE_KEY = 'flourishing-seed-inventory';
let nextSeedId = 2;
/** Firestore document IDs for seed rows (used for update/delete). */
let seedVaultDocIds = new Set();

function getSeedColumns() {
    return ['type', 'variety', 'amount', 'year', 'shelfLife', 'expiration', 'source', 'comments'];
}

function isFirestoreId(id) {
    return typeof id === 'string' && id.length > 10 && !/^\d+$/.test(id);
}

function addSeedRow(data = {}, firestoreId = null, skipSave = false) {
    const cols = getSeedColumns();
    const tbody = document.getElementById('seed-inventory-body');
    if (!tbody) return;
    const tr = document.createElement('tr');
    tr.setAttribute('data-seed-id', firestoreId != null ? String(firestoreId) : String(nextSeedId++));
    const defaults = { type: '', variety: '', amount: '', year: '', shelfLife: '', expiration: '', source: '', comments: '' };
    const row = { ...defaults, ...data };
    const placeholders = { type: 'e.g. Tomato', variety: 'Variety', amount: 'e.g. 1 Packet', year: 'Year', shelfLife: 'Shelf Life', expiration: 'Expiration', source: 'Source', comments: 'Comments' };
    tr.innerHTML = cols.map(key => {
        const val = row[key] || '';
        return `<td><input type="text" value="${escapeHtml(val)}" placeholder="${placeholders[key] || key}" /></td>`;
    }).join('') + `<td class="cell-actions"><button type="button" onclick="removeSeedRow(this)" class="row-remove-btn" title="Remove row"><i class="fa-solid fa-trash-can"></i></button></td>`;
    tbody.appendChild(tr);
    if (firestoreId) seedVaultDocIds.add(firestoreId);
    if (!skipSave) saveSeedInventory();
}

function escapeHtml(str) {
    if (str == null) return '';
    const div = document.createElement('div');
    div.textContent = String(str);
    return div.innerHTML;
}

function removeSeedRow(btn) {
    if (isPreviewMode()) return;
    const tr = btn && btn.closest ? btn.closest('tr') : null;
    const id = tr ? tr.getAttribute('data-seed-id') : null;
    if (id && isFirestoreId(id)) seedVaultDocIds.delete(id);
    if (tr) tr.remove();
    saveSeedInventory();
}

function getSeedInventoryData() {
    const rows = [];
    const tbody = document.querySelector('#seed-inventory-body');
    if (!tbody) return rows;
    tbody.querySelectorAll('tr').forEach(tr => {
        const inputs = tr.querySelectorAll('td input');
        const cols = getSeedColumns();
        const row = {};
        cols.forEach((key, i) => { row[key] = inputs[i] ? inputs[i].value.trim() : ''; });
        const id = tr.getAttribute('data-seed-id');
        if (id && isFirestoreId(id)) row.id = id;
        rows.push(row);
    });
    return rows;
}

async function saveSeedInventory() {
    if (isPreviewMode()) return;
    const uid = auth?.currentUser?.uid;
    if (!uid || !db) {
        try { localStorage.setItem(SEED_STORAGE_KEY, JSON.stringify(getSeedInventoryData().map(({ id, ...r }) => r))); } catch (e) {}
        return;
    }
    const rows = getSeedInventoryData();
    const col = collection(db, 'users', uid, 'seedVault');
    const newIds = [];
    for (const row of rows) {
        const { id, ...data } = row;
        if (id && seedVaultDocIds.has(id)) {
            await updateDoc(doc(db, 'users', uid, 'seedVault', id), data);
        } else {
            const ref = await addDoc(col, data);
            seedVaultDocIds.add(ref.id);
            newIds.push(ref.id);
        }
    }
    if (newIds.length) {
        const tbody = document.querySelector('#seed-inventory-body');
        const trs = tbody ? Array.from(tbody.querySelectorAll('tr')) : [];
        let idx = 0;
        for (const tr of trs) {
            const id = tr.getAttribute('data-seed-id');
            if (id && !isFirestoreId(id) && newIds[idx] != null) {
                tr.setAttribute('data-seed-id', newIds[idx]);
                idx++;
            }
        }
    }
    const currentIds = [...rows.filter(r => r.id && seedVaultDocIds.has(r.id)).map(r => r.id), ...newIds];
    for (const id of [...seedVaultDocIds]) {
        if (!currentIds.includes(id)) {
            await deleteDoc(doc(db, 'users', uid, 'seedVault', id));
            seedVaultDocIds.delete(id);
        }
    }
}

async function loadSeedInventory() {
    const tbody = document.getElementById('seed-inventory-body');
    if (!tbody) return;
    if (isPreviewMode()) {
        tbody.innerHTML = '';
        nextSeedId = 1;
        seedVaultDocIds = new Set();
        PREVIEW_SAMPLE_DATA.seedRows.forEach((row) => addSeedRow(row, null, true));
        return;
    }
    const uid = auth?.currentUser?.uid;
    if (!uid || !db) {
        try {
            const raw = localStorage.getItem(SEED_STORAGE_KEY);
            if (!raw) return;
            const data = JSON.parse(raw);
            tbody.innerHTML = '';
            nextSeedId = 1;
            seedVaultDocIds = new Set();
            data.forEach(row => addSeedRow(row));
        } catch (e) {}
        return;
    }
    const col = collection(db, 'users', uid, 'seedVault');
    const snap = await getDocs(col);
    tbody.innerHTML = '';
    nextSeedId = 1;
    seedVaultDocIds = new Set(snap.docs.map(d => d.id));
    if (snap.empty) {
        try {
            const raw = localStorage.getItem(SEED_STORAGE_KEY);
            if (raw) {
                const data = JSON.parse(raw);
                if (Array.isArray(data) && data.length > 0) {
                    for (const row of data) {
                        const ref = await addDoc(col, row);
                        seedVaultDocIds.add(ref.id);
                    }
                    const snap2 = await getDocs(col);
                    snap2.docs.forEach(d => addSeedRow(d.data(), d.id, true));
                    try { localStorage.removeItem(SEED_STORAGE_KEY); } catch (e2) {}
                    return;
                }
            }
        } catch (e) {}
    }
    snap.docs.forEach(d => addSeedRow(d.data(), d.id, true));
}

// --- Garden Budget (Firestore: users/{uid}/gardenBudget) ---
const BUDGET_STORAGE_KEY = 'flourishing-garden-budget';
let nextBudgetId = 1;
let gardenBudgetDocIds = new Set();

function getBudgetColumns() {
    return ['item', 'description', 'allottedBudget', 'qty', 'costPerItem'];
}

function formatBudgetTotal(qty, costPerItem) {
    const q = parseFloat(String(qty).replace(/[^0-9.-]/g, '')) || 0;
    const c = parseFloat(String(costPerItem).replace(/[^0-9.-]/g, '')) || 0;
    const total = q * c;
    if (total === 0 && (qty === '' || costPerItem === '')) return '—';
    return Number.isFinite(total) ? total.toFixed(2) : '—';
}

function updateBudgetTotals() {
    const tbody = document.querySelector('#garden-budget-body');
    if (!tbody) return;
    tbody.querySelectorAll('tr').forEach(tr => {
        const inputs = tr.querySelectorAll('td input');
        const totalCell = tr.querySelector('.budget-total-cell');
        if (inputs.length >= 5 && totalCell) {
            const qty = inputs[3].value.trim();
            const costPerItem = inputs[4].value.trim();
            totalCell.textContent = formatBudgetTotal(qty, costPerItem);
        }
    });
}

function addBudgetRow(data = {}, firestoreId = null, skipSave = false) {
    const cols = getBudgetColumns();
    const tbody = document.getElementById('garden-budget-body');
    if (!tbody) return;
    const tr = document.createElement('tr');
    tr.setAttribute('data-budget-id', firestoreId != null ? String(firestoreId) : String(nextBudgetId++));
    const defaults = { item: '', description: '', allottedBudget: '', qty: '', costPerItem: '' };
    const row = { ...defaults, ...data };
    const placeholders = { item: 'e.g. Seeds', description: 'Details', allottedBudget: '0.00', qty: '1', costPerItem: '0.00' };
    const qtyVal = row.qty !== undefined ? row.qty : '';
    const costVal = row.costPerItem !== undefined ? row.costPerItem : '';
    const totalDisplay = formatBudgetTotal(qtyVal, costVal);
    tr.innerHTML =
        `<td><input type="text" value="${escapeHtml(row.item)}" placeholder="${placeholders.item}" /></td>` +
        `<td><input type="text" value="${escapeHtml(row.description)}" placeholder="${placeholders.description}" /></td>` +
        `<td><input type="text" value="${escapeHtml(row.allottedBudget)}" placeholder="${placeholders.allottedBudget}" /></td>` +
        `<td><input type="text" value="${escapeHtml(String(qtyVal))}" placeholder="${placeholders.qty}" /></td>` +
        `<td><input type="text" value="${escapeHtml(String(costVal))}" placeholder="${placeholders.costPerItem}" /></td>` +
        `<td class="budget-total-cell">${escapeHtml(totalDisplay)}</td>` +
        `<td class="cell-actions"><button type="button" onclick="removeBudgetRow(this)" class="row-remove-btn" title="Remove row"><i class="fa-solid fa-trash-can"></i></button></td>`;
    tbody.appendChild(tr);
    if (firestoreId) gardenBudgetDocIds.add(firestoreId);
    if (!skipSave) saveBudget();
}

function removeBudgetRow(btn) {
    if (isPreviewMode()) return;
    const tr = btn && btn.closest ? btn.closest('tr') : null;
    const id = tr ? tr.getAttribute('data-budget-id') : null;
    if (id && isFirestoreId(id)) gardenBudgetDocIds.delete(id);
    if (tr) tr.remove();
    saveBudget();
}

function getBudgetData() {
    const rows = [];
    const tbody = document.querySelector('#garden-budget-body');
    if (!tbody) return rows;
    tbody.querySelectorAll('tr').forEach(tr => {
        const inputs = tr.querySelectorAll('td input');
        const cols = getBudgetColumns();
        const row = {};
        cols.forEach((key, i) => { row[key] = inputs[i] ? inputs[i].value.trim() : ''; });
        const id = tr.getAttribute('data-budget-id');
        if (id && isFirestoreId(id)) row.id = id;
        rows.push(row);
    });
    return rows;
}

function isBudgetRowEmpty(data) {
    const t = (v) => (v == null ? '' : String(v).trim());
    return !t(data.item) && !t(data.description) && !t(data.allottedBudget) && !t(data.qty) && !t(data.costPerItem);
}

async function saveBudget() {
    if (isPreviewMode()) return;
    updateBudgetTotals();
    const uid = auth?.currentUser?.uid;
    if (!uid || !db) {
        try { localStorage.setItem(BUDGET_STORAGE_KEY, JSON.stringify(getBudgetData().map(({ id, ...r }) => r))); } catch (e) {}
        return;
    }
    const rows = getBudgetData();
    const col = collection(db, 'users', uid, 'gardenBudget');
    const now = new Date().toISOString();
    const newIds = [];
    for (const row of rows) {
        const { id, ...data } = row;
        const payload = { ...data, updatedAt: now };
        if (id && gardenBudgetDocIds.has(id)) {
            await updateDoc(doc(db, 'users', uid, 'gardenBudget', id), payload);
        } else {
            const ref = await addDoc(col, payload);
            gardenBudgetDocIds.add(ref.id);
            newIds.push(ref.id);
        }
    }
    if (newIds.length) {
        const tbody = document.querySelector('#garden-budget-body');
        const trs = tbody ? Array.from(tbody.querySelectorAll('tr')) : [];
        let idx = 0;
        for (const tr of trs) {
            const id = tr.getAttribute('data-budget-id');
            if (id && !isFirestoreId(id) && newIds[idx] != null) {
                tr.setAttribute('data-budget-id', newIds[idx]);
                idx++;
            }
        }
    }
    const currentIds = [...rows.filter(r => r.id && gardenBudgetDocIds.has(r.id)).map(r => r.id), ...newIds];
    for (const id of [...gardenBudgetDocIds]) {
        if (!currentIds.includes(id)) {
            await deleteDoc(doc(db, 'users', uid, 'gardenBudget', id));
            gardenBudgetDocIds.delete(id);
        }
    }
}

async function loadBudget() {
    const tbody = document.getElementById('garden-budget-body');
    if (!tbody) return;
    if (isPreviewMode()) {
        tbody.innerHTML = '';
        nextBudgetId = 1;
        gardenBudgetDocIds = new Set();
        PREVIEW_SAMPLE_DATA.budgetRows.forEach((row) => addBudgetRow(row, null, true));
        updateBudgetTotals();
        return;
    }
    const uid = auth?.currentUser?.uid;
    if (!uid || !db) {
        try {
            const raw = localStorage.getItem(BUDGET_STORAGE_KEY);
            tbody.innerHTML = '';
            nextBudgetId = 1;
            gardenBudgetDocIds = new Set();
            if (raw) {
                const data = JSON.parse(raw);
                if (Array.isArray(data)) {
                    const sorted = [...data].sort((a, b) => {
                        const aEmpty = isBudgetRowEmpty(a);
                        const bEmpty = isBudgetRowEmpty(b);
                        if (aEmpty && !bEmpty) return 1;
                        if (!aEmpty && bEmpty) return -1;
                        return 0;
                    });
                    sorted.forEach(row => addBudgetRow(row));
                }
            }
            if (tbody.querySelectorAll('tr').length === 0) {
                for (let i = 0; i < 8; i++) addBudgetRow();
            }
        } catch (e) {
            tbody.innerHTML = '';
            nextBudgetId = 1;
            gardenBudgetDocIds = new Set();
            for (let i = 0; i < 8; i++) addBudgetRow();
        }
        return;
    }
    const col = collection(db, 'users', uid, 'gardenBudget');
    const snap = await getDocs(col);
    tbody.innerHTML = '';
    nextBudgetId = 1;
    gardenBudgetDocIds = new Set(snap.docs.map(d => d.id));
    if (snap.empty) {
        try {
            const raw = localStorage.getItem(BUDGET_STORAGE_KEY);
            if (raw) {
                const data = JSON.parse(raw);
                if (Array.isArray(data) && data.length > 0) {
                    const now = new Date().toISOString();
                    for (const row of data) {
                        const ref = await addDoc(col, { ...row, updatedAt: now });
                        gardenBudgetDocIds.add(ref.id);
                    }
                    const snap2 = await getDocs(col);
                    const sorted = [...snap2.docs].sort((a, b) => {
                        const aEmpty = isBudgetRowEmpty(a.data());
                        const bEmpty = isBudgetRowEmpty(b.data());
                        if (aEmpty && !bEmpty) return 1;
                        if (!aEmpty && bEmpty) return -1;
                        if (aEmpty && bEmpty) return 0;
                        const aT = a.data().updatedAt || '';
                        const bT = b.data().updatedAt || '';
                        return bT.localeCompare(aT);
                    });
                    sorted.forEach(d => addBudgetRow(d.data(), d.id, true));
                    try { localStorage.removeItem(BUDGET_STORAGE_KEY); } catch (e2) {}
                    return;
                }
            }
        } catch (e) {}
    }
    const sorted = [...snap.docs].sort((a, b) => {
        const aEmpty = isBudgetRowEmpty(a.data());
        const bEmpty = isBudgetRowEmpty(b.data());
        if (aEmpty && !bEmpty) return 1;
        if (!aEmpty && bEmpty) return -1;
        if (aEmpty && bEmpty) return 0;
        const aT = a.data().updatedAt || '';
        const bT = b.data().updatedAt || '';
        return bT.localeCompare(aT);
    });
    sorted.forEach(d => addBudgetRow(d.data(), d.id, true));
}

// --- Harvest Log (Firestore: users/{uid}/harvestLog; notes: users/{uid}/harvestLog/harvestNotes) ---
const HARVEST_STORAGE_KEY = 'flourishing-harvest-log';
const HARVEST_NOTES_KEY = 'flourishing-harvest-notes';
const HARVEST_NOTES_DOC_ID = 'harvestNotes';
let nextHarvestId = 1;
let harvestLogDocIds = new Set();

function getHarvestColumns() {
    return ['fruitOrVegetable', 'variety', 'weightQty', 'harvested'];
}

function addHarvestRow(data = {}, firestoreId = null, skipSave = false) {
    const cols = getHarvestColumns();
    const tbody = document.getElementById('harvest-log-body');
    if (!tbody) return;
    const tr = document.createElement('tr');
    tr.setAttribute('data-harvest-id', firestoreId != null ? String(firestoreId) : String(nextHarvestId++));
    const defaults = { fruitOrVegetable: '', variety: '', weightQty: '', harvested: '' };
    const row = { ...defaults, ...data };
    const placeholders = { fruitOrVegetable: 'e.g. Tomato', variety: 'Variety', weightQty: 'e.g. 5 lbs', harvested: 'Date' };
    tr.innerHTML = cols.map(key => {
        const val = row[key] || '';
        return `<td><input type="text" value="${escapeHtml(val)}" placeholder="${placeholders[key] || key}" /></td>`;
    }).join('') + `<td class="cell-actions"><button type="button" onclick="removeHarvestRow(this)" class="row-remove-btn" title="Remove row"><i class="fa-solid fa-trash-can"></i></button></td>`;
    tbody.appendChild(tr);
    if (firestoreId) harvestLogDocIds.add(firestoreId);
    if (!skipSave) saveHarvestLog();
}

function removeHarvestRow(btn) {
    if (isPreviewMode()) return;
    const tr = btn && btn.closest ? btn.closest('tr') : null;
    const id = tr ? tr.getAttribute('data-harvest-id') : null;
    if (id && isFirestoreId(id)) harvestLogDocIds.delete(id);
    if (tr) tr.remove();
    saveHarvestLog();
}

function getHarvestLogData() {
    const rows = [];
    const tbody = document.querySelector('#harvest-log-body');
    if (!tbody) return rows;
    tbody.querySelectorAll('tr').forEach(tr => {
        const inputs = tr.querySelectorAll('td input');
        const cols = getHarvestColumns();
        const row = {};
        cols.forEach((key, i) => { row[key] = inputs[i] ? inputs[i].value.trim() : ''; });
        const id = tr.getAttribute('data-harvest-id');
        if (id && isFirestoreId(id)) row.id = id;
        rows.push(row);
    });
    return rows;
}

function isHarvestRowEmpty(data) {
    const t = (v) => (v == null ? '' : String(v).trim());
    return !t(data.fruitOrVegetable) && !t(data.variety) && !t(data.weightQty) && !t(data.harvested);
}

async function saveHarvestLog() {
    if (isPreviewMode()) return;
    const uid = auth?.currentUser?.uid;
    if (!uid || !db) {
        try { localStorage.setItem(HARVEST_STORAGE_KEY, JSON.stringify(getHarvestLogData().map(({ id, ...r }) => r))); } catch (e) {}
        return;
    }
    const rows = getHarvestLogData();
    const col = collection(db, 'users', uid, 'harvestLog');
    const now = new Date().toISOString();
    const newIds = [];
    for (const row of rows) {
        const { id, ...data } = row;
        const payload = { ...data, updatedAt: now };
        if (id && harvestLogDocIds.has(id)) {
            await updateDoc(doc(db, 'users', uid, 'harvestLog', id), payload);
        } else {
            const ref = await addDoc(col, payload);
            harvestLogDocIds.add(ref.id);
            newIds.push(ref.id);
        }
    }
    if (newIds.length) {
        const tbody = document.querySelector('#harvest-log-body');
        const trs = tbody ? Array.from(tbody.querySelectorAll('tr')) : [];
        let idx = 0;
        for (const tr of trs) {
            const id = tr.getAttribute('data-harvest-id');
            if (id && !isFirestoreId(id) && newIds[idx] != null) {
                tr.setAttribute('data-harvest-id', newIds[idx]);
                idx++;
            }
        }
    }
    const currentIds = [...rows.filter(r => r.id && harvestLogDocIds.has(r.id)).map(r => r.id), ...newIds];
    for (const id of [...harvestLogDocIds]) {
        if (!currentIds.includes(id) && id !== HARVEST_NOTES_DOC_ID) {
            await deleteDoc(doc(db, 'users', uid, 'harvestLog', id));
            harvestLogDocIds.delete(id);
        }
    }
}

let harvestNotesEntries = [];
let editingNoteIndex = -1;

async function persistHarvestNotes() {
    if (isPreviewMode()) return;
    const uid = auth?.currentUser?.uid;
    if (uid && db) {
        const notesRef = doc(db, 'users', uid, 'harvestLog', HARVEST_NOTES_DOC_ID);
        await setDoc(notesRef, { entries: harvestNotesEntries }, { merge: true });
    } else {
        try { localStorage.setItem(HARVEST_NOTES_KEY, JSON.stringify(harvestNotesEntries)); } catch (e) {}
    }
}

async function saveHarvestNote() {
    if (isPreviewMode()) return;
    const input = document.getElementById('harvest-notes-input');
    if (!input) return;
    const text = input.value.trim();
    if (!text) return;
    harvestNotesEntries.unshift({ date: new Date().toISOString(), text });
    await persistHarvestNotes();
    input.value = '';
    renderHarvestNotes();
}

function formatNoteDate(iso) {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    if (sameDay) return 'Today, ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return d.toLocaleDateString([], { dateStyle: 'medium' }) + ' ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function renderHarvestNotes() {
    const container = document.getElementById('harvest-notes-entries');
    const emptyEl = document.getElementById('harvest-notes-log-empty');
    if (!container) return;
    if (harvestNotesEntries.length === 0) {
        if (emptyEl) emptyEl.style.display = 'block';
        container.innerHTML = '';
        editingNoteIndex = -1;
        return;
    }
    if (emptyEl) emptyEl.style.display = 'none';
    container.innerHTML = harvestNotesEntries.map((entry, i) => {
        const canEditNotes = !isPreviewMode();
        if (i === editingNoteIndex) {
            return `
                <div class="harvest-notes-log-entry harvest-notes-log-entry-editing" data-index="${i}">
                    <div class="harvest-notes-log-date">${escapeHtml(formatNoteDate(entry.date))}</div>
                    <textarea class="harvest-notes-edit-textarea" rows="3" data-index="${i}">${escapeHtml(entry.text)}</textarea>
                    <div class="harvest-notes-entry-actions">
                        <button type="button" class="harvest-notes-btn harvest-notes-btn-save" onclick="saveHarvestNoteEdit(event)" title="Save">
                            <i class="fa-solid fa-check"></i> Save
                        </button>
                        <button type="button" class="harvest-notes-btn harvest-notes-btn-cancel" onclick="cancelHarvestNoteEdit()" title="Cancel">
                            <i class="fa-solid fa-xmark"></i> Cancel
                        </button>
                    </div>
                </div>
            `;
        }
        return `
            <div class="harvest-notes-log-entry" data-index="${i}">
                <div class="harvest-notes-log-entry-content">
                    <div class="harvest-notes-log-date">${escapeHtml(formatNoteDate(entry.date))}</div>
                    <div class="harvest-notes-log-text">${escapeHtml(entry.text)}</div>
                </div>
                ${canEditNotes ? `
                    <div class="harvest-notes-entry-actions">
                        <button type="button" class="harvest-notes-btn harvest-notes-btn-edit" onclick="editHarvestNote(event)" title="Edit note">
                            <i class="fa-solid fa-pen"></i>
                        </button>
                        <button type="button" class="harvest-notes-btn harvest-notes-btn-delete" onclick="deleteHarvestNote(event)" title="Delete note">
                            <i class="fa-solid fa-trash-can"></i>
                        </button>
                    </div>
                ` : ''}
            </div>
        `;
    }).join('');
}

function editHarvestNote(ev) {
    if (isPreviewMode()) return;
    const entryEl = ev.target.closest('.harvest-notes-log-entry');
    if (!entryEl) return;
    const idx = parseInt(entryEl.getAttribute('data-index'), 10);
    if (Number.isNaN(idx) || idx < 0 || idx >= harvestNotesEntries.length) return;
    editingNoteIndex = idx;
    renderHarvestNotes();
}

function cancelHarvestNoteEdit() {
    editingNoteIndex = -1;
    renderHarvestNotes();
}

async function saveHarvestNoteEdit(ev) {
    if (isPreviewMode()) return;
    const entryEl = ev.target.closest('.harvest-notes-log-entry');
    if (!entryEl) return;
    const idx = parseInt(entryEl.getAttribute('data-index'), 10);
    if (Number.isNaN(idx) || idx < 0 || idx >= harvestNotesEntries.length) return;
    const textarea = entryEl.querySelector('.harvest-notes-edit-textarea');
    const text = textarea ? textarea.value.trim() : '';
    if (text) {
        harvestNotesEntries[idx].text = text;
        await persistHarvestNotes();
    }
    editingNoteIndex = -1;
    renderHarvestNotes();
}

async function deleteHarvestNote(ev) {
    if (isPreviewMode()) return;
    const entryEl = ev.target.closest('.harvest-notes-log-entry');
    if (!entryEl) return;
    const idx = parseInt(entryEl.getAttribute('data-index'), 10);
    if (Number.isNaN(idx) || idx < 0 || idx >= harvestNotesEntries.length) return;
    if (!confirm('Delete this note?')) return;
    harvestNotesEntries.splice(idx, 1);
    await persistHarvestNotes();
    if (editingNoteIndex === idx) editingNoteIndex = -1;
    else if (editingNoteIndex > idx) editingNoteIndex--;
    renderHarvestNotes();
}

async function loadHarvestNotes() {
    if (isPreviewMode()) {
        harvestNotesEntries = PREVIEW_SAMPLE_DATA.harvestNotes.map((entry) => ({ ...entry }));
        renderHarvestNotes();
        return;
    }
    const uid = auth?.currentUser?.uid;
    if (uid && db) {
        try {
            const notesRef = doc(db, 'users', uid, 'harvestLog', HARVEST_NOTES_DOC_ID);
            const snap = await getDoc(notesRef);
            const data = snap.data();
            harvestNotesEntries = Array.isArray(data?.entries) ? data.entries : [];
            if (harvestNotesEntries.length === 0) {
                const raw = localStorage.getItem(HARVEST_NOTES_KEY);
                if (raw) {
                    try {
                        const parsed = JSON.parse(raw);
                        const migrated = Array.isArray(parsed) ? parsed : (parsed ? [parsed] : []);
                        if (migrated.length && typeof migrated[0] === 'string') {
                            harvestNotesEntries = [{ date: new Date().toISOString(), text: migrated[0] }];
                        } else if (migrated.length && !migrated[0].date) {
                            harvestNotesEntries = migrated.map(t => ({ date: new Date().toISOString(), text: typeof t === 'string' ? t : (t && t.text) || '' }));
                        } else {
                            harvestNotesEntries = migrated;
                        }
                        if (harvestNotesEntries.length) {
                            await setDoc(notesRef, { entries: harvestNotesEntries }, { merge: true });
                            try { localStorage.removeItem(HARVEST_NOTES_KEY); } catch (e2) {}
                        }
                    } catch (e2) {}
                }
            }
        } catch (e) {
            harvestNotesEntries = [];
        }
    } else {
        try {
            const raw = localStorage.getItem(HARVEST_NOTES_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                harvestNotesEntries = Array.isArray(parsed) ? parsed : (parsed ? [parsed] : []);
                if (harvestNotesEntries.length && typeof harvestNotesEntries[0] === 'string') {
                    harvestNotesEntries = [{ date: new Date().toISOString(), text: harvestNotesEntries[0] }];
                } else if (harvestNotesEntries.length && !harvestNotesEntries[0].date) {
                    harvestNotesEntries = harvestNotesEntries.map(t => ({ date: new Date().toISOString(), text: typeof t === 'string' ? t : (t && t.text) || '' }));
                }
            } else {
                harvestNotesEntries = [];
            }
        } catch (e) {
            harvestNotesEntries = [];
        }
    }
    renderHarvestNotes();
}

async function loadHarvestLog() {
    const tbody = document.getElementById('harvest-log-body');
    if (!tbody) return;
    if (isPreviewMode()) {
        tbody.innerHTML = '';
        nextHarvestId = 1;
        harvestLogDocIds = new Set();
        PREVIEW_SAMPLE_DATA.harvestRows.forEach((row) => addHarvestRow(row, null, true));
        await loadHarvestNotes();
        return;
    }
    const uid = auth?.currentUser?.uid;
    if (!uid || !db) {
        try {
            const raw = localStorage.getItem(HARVEST_STORAGE_KEY);
            tbody.innerHTML = '';
            nextHarvestId = 1;
            harvestLogDocIds = new Set();
            if (raw) {
                const data = JSON.parse(raw);
                if (Array.isArray(data)) {
                    const sorted = [...data].sort((a, b) => {
                        const aEmpty = isHarvestRowEmpty(a);
                        const bEmpty = isHarvestRowEmpty(b);
                        if (aEmpty && !bEmpty) return 1;
                        if (!aEmpty && bEmpty) return -1;
                        return 0;
                    });
                    sorted.forEach(row => addHarvestRow(row));
                }
            }
            if (tbody.querySelectorAll('tr').length === 0) {
                for (let i = 0; i < 6; i++) addHarvestRow();
            }
        } catch (e) {
            tbody.innerHTML = '';
            nextHarvestId = 1;
            for (let i = 0; i < 6; i++) addHarvestRow();
        }
        await loadHarvestNotes();
        return;
    }
    const col = collection(db, 'users', uid, 'harvestLog');
    const snap = await getDocs(col);
    tbody.innerHTML = '';
    nextHarvestId = 1;
    const harvestDocs = snap.docs.filter(d => d.id !== HARVEST_NOTES_DOC_ID);
    harvestLogDocIds = new Set(harvestDocs.map(d => d.id));

    function sortHarvestDocs(docs) {
        return [...docs].sort((a, b) => {
            const aEmpty = isHarvestRowEmpty(a.data());
            const bEmpty = isHarvestRowEmpty(b.data());
            if (aEmpty && !bEmpty) return 1;
            if (!aEmpty && bEmpty) return -1;
            if (aEmpty && bEmpty) return 0;
            const aT = a.data().updatedAt || '';
            const bT = b.data().updatedAt || '';
            return bT.localeCompare(aT);
        });
    }

    if (harvestDocs.length === 0) {
        try {
            const raw = localStorage.getItem(HARVEST_STORAGE_KEY);
            if (raw) {
                const data = JSON.parse(raw);
                if (Array.isArray(data) && data.length > 0) {
                    const now = new Date().toISOString();
                    for (const row of data) {
                        const ref = await addDoc(col, { ...row, updatedAt: now });
                        harvestLogDocIds.add(ref.id);
                    }
                    const snap2 = await getDocs(col);
                    const tableDocs = snap2.docs.filter(d => d.id !== HARVEST_NOTES_DOC_ID);
                    sortHarvestDocs(tableDocs).forEach(d => addHarvestRow(d.data(), d.id, true));
                    try { localStorage.removeItem(HARVEST_STORAGE_KEY); } catch (e2) {}
                }
            }
        } catch (e) {}
    }
    if (harvestDocs.length > 0) {
        sortHarvestDocs(harvestDocs).forEach(d => addHarvestRow(d.data(), d.id, true));
    }
    await loadHarvestNotes();
}

function initializeDashboardUiOnce() {
    if (dashboardStaticUiInitialized) return;
    dashboardStaticUiInitialized = true;

    initPlantCardSearch();
    initDatePickerSync();
    initEntryPhotoHandlers();
    initGlobalCalendarHandlers();

    const seedBody = document.getElementById('seed-inventory-body');
    seedBody && seedBody.addEventListener('input', () => saveSeedInventory());
    const budgetBody = document.getElementById('garden-budget-body');
    budgetBody && budgetBody.addEventListener('input', () => saveBudget());
    const harvestBody = document.getElementById('harvest-log-body');
    harvestBody && harvestBody.addEventListener('input', () => saveHarvestLog());

    const pictureEl = document.getElementById('journal-garden-picture');
    if (pictureEl) {
        pictureEl.addEventListener('click', () => {
            const img = pictureEl.querySelector('img');
            if (img && img.src) openJournalPictureOverlay(img.src, img.alt || '');
        });
        pictureEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const img = pictureEl.querySelector('img');
                if (img && img.src) openJournalPictureOverlay(img.src, img.alt || '');
            }
        });
    }
    const plantInfoPictureEl = document.getElementById('journal-plant-info-picture');
    if (plantInfoPictureEl) {
        plantInfoPictureEl.addEventListener('click', () => {
            const img = plantInfoPictureEl.querySelector('img');
            if (img && img.src) openJournalPictureOverlay(img.src, img.alt || '');
        });
        plantInfoPictureEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const img = plantInfoPictureEl.querySelector('img');
                if (img && img.src) openJournalPictureOverlay(img.src, img.alt || '');
            }
        });
    }
    const calendarPictureEl = document.getElementById('journal-calendar-picture');
    if (calendarPictureEl) {
        calendarPictureEl.addEventListener('click', () => {
            const img = calendarPictureEl.querySelector('img');
            if (img && img.src) openJournalPictureOverlay(img.src, img.alt || '');
        });
        calendarPictureEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const img = calendarPictureEl.querySelector('img');
                if (img && img.src) openJournalPictureOverlay(img.src, img.alt || '');
            }
        });
    }
    document.addEventListener('click', (e) => {
        const link = e.target.closest('.dashboard-journal-variety-link');
        if (!link) return;
        e.preventDefault();
        const planId = link.getAttribute('data-plan-id');
        if (!planId) return;
        openJournal(planId, true);
    });

    window.addEventListener('hashchange', () => {
        const app = document.getElementById('dashboard-app');
        if (!app || app.classList.contains('hidden')) return;
        const tab = getDashboardTabIdFromHash();
        if (tab) showTab(tab);
    });
}

function applyDashboardModeInteractivity() {
    const preview = isPreviewMode();
    const plantSearchInput = document.getElementById('plant-card-search');
    const harvestNotesInput = document.getElementById('harvest-notes-input');
    const harvestNotesSaveBtn = document.querySelector('.harvest-notes-save-btn');
    const newEntryBtn = document.querySelector('.dashboard-btn-new-entry');
    const addRowButtons = document.querySelectorAll('.seed-add-row, .garden-budget-add-row, .harvest-add-row');

    document.querySelectorAll('#seed-inventory-body input, #garden-budget-body input, #harvest-log-body input').forEach((input) => {
        input.readOnly = preview;
        input.classList.toggle('dashboard-readonly-input', preview);
    });

    if (harvestNotesInput) {
        harvestNotesInput.readOnly = preview;
        harvestNotesInput.placeholder = preview
            ? 'Preview only. Sign in and upgrade to add your own notes.'
            : 'Add a note about your harvest…';
        harvestNotesInput.classList.toggle('dashboard-readonly-input', preview);
    }

    if (harvestNotesSaveBtn) harvestNotesSaveBtn.disabled = preview;
    if (newEntryBtn) newEntryBtn.hidden = preview;
    addRowButtons.forEach((btn) => {
        btn.hidden = preview;
        btn.disabled = preview;
    });
    if (plantSearchInput) {
        // Plant search: use subscription as source of truth so premium users always get search
        const isPremium = (subscriptionManager.getSubscriptionInfo?.() ?? {}).isPremium === true;
        const lockPlantSearch = !isPremium;
        plantSearchInput.disabled = lockPlantSearch;
        plantSearchInput.readOnly = lockPlantSearch;
        plantSearchInput.classList.toggle('dashboard-readonly-input', lockPlantSearch);
        plantSearchInput.placeholder = lockPlantSearch
            ? 'Plant search unlocks after you sign in and upgrade.'
            : 'Start typing a plant name...';
    }
}

async function loadDashboardForCurrentMode() {
    const loadId = ++activeDashboardLoadId;

    closePlantCardSuggestions();
    closeJournal();
    editingEntryIndex = null;
    editingNoteIndex = -1;
    pendingRemovePlanId = null;
    pendingRemoveYear = null;

    const newEntryModal = document.getElementById('new-entry-modal');
    const removePlanModal = document.getElementById('remove-plan-modal');
    const removeYearModal = document.getElementById('remove-year-modal');
    if (newEntryModal) newEntryModal.classList.remove('show');
    if (removePlanModal) removePlanModal.classList.remove('show');
    if (removeYearModal) removeYearModal.classList.remove('show');
    clearEntryPhotoInModal();

    if (isPreviewMode()) {
        loadPreviewGardenPlans();
        renderYearRow();
        renderPlans();
        await loadSeedInventory();
        if (loadId !== activeDashboardLoadId) return;
        await loadBudget();
        if (loadId !== activeDashboardLoadId) return;
        await loadHarvestLog();
        if (loadId !== activeDashboardLoadId) return;
        renderSelectedGlobalCalendar({ useCurrentDate: true });
        configurePlantCardSearchForCurrentMode();
        applyDashboardModeInteractivity();
        applyDashboardTabFromLocationHash();
        return;
    }

    resetDashboardDataState();
    await loadGardenPlans();
    if (loadId !== activeDashboardLoadId) return;
    const restoredCalendarCache = restoreJournalCache();
    renderYearRow();
    renderPlans();
    if (restoredCalendarCache) {
        renderSelectedGlobalCalendar();
    } else {
        renderSelectedGlobalCalendar({ useCurrentDate: true });
    }
    preloadCalendarEntries({ force: restoredCalendarCache }).then(() => {
        if (loadId === activeDashboardLoadId) refreshCalendarIfVisible();
    }).catch(() => {});
    await loadSeedInventory();
    if (loadId !== activeDashboardLoadId) return;
    await loadBudget();
    if (loadId !== activeDashboardLoadId) return;
    await loadHarvestLog();
    if (loadId !== activeDashboardLoadId) return;
    configurePlantCardSearchForCurrentMode();
    applyDashboardModeInteractivity();
    applyDashboardTabFromLocationHash();
}

async function init() {
    initializeDashboardUiOnce();
    await loadDashboardForCurrentMode();
}

// Expose to window for inline onclick handlers
window.showTab = showTab;
window.filterYear = filterYear;
window.openJournal = openJournal;
window.closeJournal = closeJournal;
window.openNewEntryModal = openNewEntryModal;
window.closeNewEntryModal = closeNewEntryModal;
window.openJournalPictureOverlay = openJournalPictureOverlay;
window.closeJournalPictureOverlay = closeJournalPictureOverlay;
window.closeJournalDateEntriesOverlay = closeJournalDateEntriesOverlay;
window.submitNewEntry = submitNewEntry;
window.openEditEntryModal = openEditEntryModal;
window.deleteJournalEntry = deleteJournalEntry;
window.removePlan = removePlan;
window.addUpcomingYear = addUpcomingYear;
window.confirmRemoveYear = confirmRemoveYear;
window.closeRemoveYearModal = closeRemoveYearModal;
window.removeYear = removeYear;
window.confirmRemovePlan = confirmRemovePlan;
window.closeRemovePlanModal = closeRemovePlanModal;
window.addSeedRow = addSeedRow;
window.removeSeedRow = removeSeedRow;
window.addBudgetRow = addBudgetRow;
window.removeBudgetRow = removeBudgetRow;
window.addHarvestRow = addHarvestRow;
window.removeHarvestRow = removeHarvestRow;
window.saveHarvestNote = saveHarvestNote;
window.editHarvestNote = editHarvestNote;
window.saveHarvestNoteEdit = saveHarvestNoteEdit;
window.cancelHarvestNoteEdit = cancelHarvestNoteEdit;
window.deleteHarvestNote = deleteHarvestNote;

document.addEventListener('DOMContentLoaded', () => {
    showGateLoading();
    setupPremiumGate();
    // Copy main app user avatar/login behavior into dashboard header
    // (requires the same DOM IDs: #user-avatar-section, #login-prompt, #show-auth-modal, #user-avatar, #nav-username, #nav-signout)
    setTimeout(() => {
        try {
            initNavAuth();
        } catch (e) {
            // Dashboard still functions without nav auth; do not block load
            console.error('Dashboard initNavAuth failed:', e);
            document.body.classList.add('auth-ready');
        }
    }, 100);
});
